import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import BankVerification from '@/pages/BankVerification';
import Communications, { ApplicationMessages } from '@/pages/Communications';
import ActivityLog from '@/pages/ActivityLog';
import LoanHistoryPopover from '@/pages/LoanHistoryPopover';
import DebiCheckTag from '@/pages/DebiCheckTag';
import MandateCollections from '@/pages/MandateCollections';
import AgentsPanel, { AgentCell } from '@/pages/AgentsPanel';
import AccessMatrix from '@/pages/AccessMatrix';
import { ChatThreadInline } from '@/pages/ChatPanel';
import ChatSettings from '@/pages/ChatSettings';
import { MigrationCard } from '@/components/MigrationCard';
import { VerifyNowPanel } from '@/components/VerifyNowPanel';
import { VerifyNowSettings } from '@/components/VerifyNowSettings';
import { AutoDeclineCard } from '@/components/AutoDeclineCard';
import DraftsPanel from '@/pages/DraftsPanel';
import PipelinePanel from '@/pages/PipelinePanel';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart as RPieChart, Pie, Cell, Legend } from 'recharts';
import { 
  Banknote, LogOut, Users, FileText, Clock, CheckCircle2, XCircle, CreditCard,
  TrendingUp, TrendingDown, Calendar, DollarSign, Shield, RefreshCw, Filter,
  Plus, Download, Eye, AlertTriangle, PieChart, BarChart3, Send, UserPlus, 
  Package, Search, ArrowUpRight, ArrowDownRight, Wallet, Target, Activity, 
  Briefcase, Phone, MessageSquare, FileCheck, AlertCircle, Settings, 
  ChevronRight, History, ClipboardList, ShieldCheck, Link2, Copy, Check,
  MessageCircle, Pencil, Lock, ToggleLeft, ToggleRight, Trash2, ShieldPlus, PlusCircle, KeyRound, Zap
} from 'lucide-react';
import PreApprovalCheck from '@/components/PreApprovalCheck';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status configurations
const STATUS_CONFIG = {
  draft: { color: 'bg-slate-100 text-slate-700', icon: FileText, label: 'Draft' },
  submitted: { color: 'bg-blue-100 text-blue-700', icon: Send, label: 'Submitted' },
  checks_pending: { color: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Checks Pending' },
  checks_complete: { color: 'bg-cyan-100 text-cyan-700', icon: FileCheck, label: 'Checks Complete' },
  pending_decision: { color: 'bg-purple-100 text-purple-700', icon: ClipboardList, label: 'Pending Decision' },
  approved: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2, label: 'Approved' },
  declined: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Declined' },
  contract_sent: { color: 'bg-indigo-100 text-indigo-700', icon: FileText, label: 'Contract Sent' },
  contract_signed: { color: 'bg-teal-100 text-teal-700', icon: CheckCircle2, label: 'Contract Signed' },
  pending_disbursement: { color: 'bg-orange-100 text-orange-700', icon: Clock, label: 'Pending Disbursement' },
  disbursed: { color: 'bg-violet-100 text-violet-700', icon: Send, label: 'Disbursed' },
  active: { color: 'bg-green-100 text-green-700', icon: Activity, label: 'Active' },
  settled: { color: 'bg-slate-100 text-slate-600', icon: CheckCircle2, label: 'Settled' },
  defaulted: { color: 'bg-red-200 text-red-800', icon: AlertTriangle, label: 'Defaulted' },
  written_off: { color: 'bg-gray-200 text-gray-700', icon: XCircle, label: 'Written Off' }
};

const ARREARS_CONFIG = {
  current: { color: 'bg-emerald-500', label: 'Current' },
  '1-7_days': { color: 'bg-yellow-400', label: '1-7 Days' },
  '8-30_days': { color: 'bg-amber-500', label: '8-30 Days' },
  '31-60_days': { color: 'bg-orange-500', label: '31-60 Days' },
  '61-90_days': { color: 'bg-red-400', label: '61-90 Days' },
  '90+_days': { color: 'bg-red-600', label: '90+ Days' }
};

const ROLE_COLORS = {
  admin: 'bg-purple-100 text-purple-700',
  loan_officer: 'bg-blue-100 text-blue-700',
  approver: 'bg-emerald-100 text-emerald-700',
  collections: 'bg-amber-100 text-amber-700',
  finance: 'bg-cyan-100 text-cyan-700',
  support: 'bg-slate-100 text-slate-700',
  sales_agent: 'bg-indigo-100 text-indigo-700'
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader, impersonate } = useAuth();
  const [activeTab, setActiveTab] = useState(() => {
    const mods = JSON.parse(localStorage.getItem('user') || '{}')?.modules || {};
    if (mods.overview !== false) return 'overview';
    if (mods.applications) return 'customer-apps';
    if (mods.bank_verification) return 'verification';
    return 'overview';
  });

  // User management state
  const [allUsers, setAllUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [editUserDialog, setEditUserDialog] = useState({ open: false, user: null });
  const [editUserForm, setEditUserForm] = useState({});
  const [resetPwDialog, setResetPwDialog] = useState({ open: false, user: null });
  const [resetPwValue, setResetPwValue] = useState('');
  const [tempPwResult, setTempPwResult] = useState(null);
  const [generatingPw, setGeneratingPw] = useState(false);
  const [masterPwStatus, setMasterPwStatus] = useState(null);
  const [masterPwValue, setMasterPwValue] = useState('');
  const [savingMasterPw, setSavingMasterPw] = useState(false);
  const [approvedAmtDialog, setApprovedAmtDialog] = useState({ open: false, app: null });
  const [approvedAmtValue, setApprovedAmtValue] = useState('');
  const [savingApprovedAmt, setSavingApprovedAmt] = useState(false);

  const handleSaveApprovedAmount = async () => {
    const a = approvedAmtDialog.app;
    if (!a) return;
    setSavingApprovedAmt(true);
    try {
      const res = await axios.put(`${API_URL}/admin/customer-applications/${a.id}/approved-amount`,
        { approved_amount: parseFloat(approvedAmtValue) }, getAuthHeader());
      toast.success(`Approved amount set to R${res.data.approved_amount.toLocaleString()} — new total repayment R${res.data.total_repayment.toLocaleString()}`);
      setApprovedAmtDialog({ open: false, app: null });
      fetchCustomerApps();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update approved amount'); }
    setSavingApprovedAmt(false);
  };

  // Roles management state
  const [allRoles, setAllRoles] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);
  const [editRoleDialog, setEditRoleDialog] = useState({ open: false, role: null });
  const [roleForm, setRoleForm] = useState({ name: '', label: '', modules: {} });
  const [createRoleMode, setCreateRoleMode] = useState(false);

  // User modules
  const userModules = user?.modules || {};
  const hasModule = (key) => userModules[key] === true || (user?.role === 'admin' && userModules[key] !== false);

  // Redirect away from tabs the user's role cannot see
  useEffect(() => {
    if (!user) return;
    const TAB_MODULE = {
      overview: 'overview', origination: 'origination', servicing: 'origination',
      collections: 'origination', borrowers: 'origination', reports: 'origination',
      'bank-verification': 'bank_verification', 'customer-apps': 'applications',
      communications: 'communications', users: 'users', agents: 'agents',
      settings: 'settings', 'activity-log': 'audit',
    };
    if (activeTab === 'access') {
      if (user.role !== 'admin') setActiveTab('overview');
      return;
    }
    const mod = TAB_MODULE[activeTab];
    if (mod && !hasModule(mod)) {
      const order = ['overview', 'customer-apps', 'bank-verification', 'origination', 'communications', 'users', 'agents', 'settings', 'activity-log'];
      const first = order.find(t => hasModule(TAB_MODULE[t]));
      setActiveTab(first || (user.role === 'admin' ? 'access' : 'no-access'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activeTab]);
  const [isLoading, setIsLoading] = useState(true);
  const [linkCopied, setLinkCopied] = useState(false);
  const [applyLink, setApplyLink] = useState('');
  const [applyLinkSaving, setApplyLinkSaving] = useState(false);
  const [unreadReplies, setUnreadReplies] = useState(0);
  
  // Data
  const [metrics, setMetrics] = useState(null);
  const [preApprovalOpen, setPreApprovalOpen] = useState(false);
  const [filterDebiCheck, setFilterDebiCheck] = useState('all');
  const [filterPaidOut, setFilterPaidOut] = useState('all');
  const [loans, setLoans] = useState([]);
  const [borrowers, setBorrowers] = useState([]);
  const [products, setProducts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [staff, setStaff] = useState([]);
  const [arrearsLoans, setArrearsLoans] = useState([]);
  const [disbursementsReport, setDisbursementsReport] = useState([]);
  const [collectionsReport, setCollectionsReport] = useState([]);
  
  // Filters
  const [loanStatusFilter, setLoanStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [arrearsFilter, setArrearsFilter] = useState('all');
  
  // Dialogs
  const [loanDetailDialog, setLoanDetailDialog] = useState({ open: false, loan: null });
  const [paymentDialog, setPaymentDialog] = useState({ open: false, loan: null });
  const [disbursementDialog, setDisbursementDialog] = useState({ open: false, loan: null });
  const [statusDialog, setStatusDialog] = useState({ open: false, loan: null, newStatus: '' });
  const [noteDialog, setNoteDialog] = useState({ open: false, loan: null });
  const [ptpDialog, setPtpDialog] = useState({ open: false, loan: null });
  const [productDialog, setProductDialog] = useState({ open: false });
  const [staffDialog, setStaffDialog] = useState({ open: false });
  const [borrowerDialog, setBorrowerDialog] = useState({ open: false, borrower: null });
  
  // Form states
  const [paymentForm, setPaymentForm] = useState({ amount: '', method: 'EFT', reference: '' });
  const [disbursementAmount, setDisbursementAmount] = useState('');
  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState('general');
  const [ptpForm, setPtpForm] = useState({ amount: '', date: '', notes: '' });
  const [statusNotes, setStatusNotes] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  
  const [newProduct, setNewProduct] = useState({
    name: '', description: '', code: '', min_amount: 500, max_amount: 5000, default_amount: 2000,
    interest_rate: 5, initiation_fee: 250, service_fee: 69, late_penalty_rate: 10,
    min_term_days: 7, max_term_days: 90, default_term_days: 30, schedule_frequency: 'monthly',
    allocation_order: 'fees_interest_principal', max_dti_ratio: 50, max_active_loans: 1, min_income_required: 3500
  });
  
  const [newStaff, setNewStaff] = useState({ email: '', password: '', full_name: '', phone: '', role: 'loan_officer' });

  // SEO Settings
  const [seoSettings, setSeoSettings] = useState(null);
  const [seoSaving, setSeoSaving] = useState(false);
  const [siteStats, setSiteStats] = useState(null);

  // Customer Applications
  const [customerApps, setCustomerApps] = useState([]);
  const [customerAppDetail, setCustomerAppDetail] = useState({ open: false, app: null });
  const [customerAppStatusUpdate, setCustomerAppStatusUpdate] = useState('');
  const [customerAppNote, setCustomerAppNote] = useState('');
  const [selectedApps, setSelectedApps] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterAmountMin, setFilterAmountMin] = useState('');
  const [filterAmountMax, setFilterAmountMax] = useState('');

  // Dashboard charts
  const [dashStats, setDashStats] = useState(null);

  const fetchSeo = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/seo`, getAuthHeader());
      setSeoSettings(res.data);
    } catch { /* ignore */ }
  }, [getAuthHeader]);

  const saveSeo = async () => {
    setSeoSaving(true);
    try {
      const res = await axios.put(`${API_URL}/seo`, seoSettings, getAuthHeader());
      setSeoSettings(res.data);
      toast.success('SEO settings saved successfully');
    } catch {
      toast.error('Failed to save SEO settings');
    } finally {
      setSeoSaving(false);
    }
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const headers = getAuthHeader();
      const results = await Promise.allSettled([
        axios.get(`${API_URL}/reports/portfolio`, headers),
        axios.get(`${API_URL}/loans${loanStatusFilter !== 'all' ? `?status=${loanStatusFilter}` : ''}`, headers),
        axios.get(`${API_URL}/borrowers`, headers),
        axios.get(`${API_URL}/products?active_only=false`, headers),
        axios.get(`${API_URL}/payments`, headers),
        axios.get(`${API_URL}/staff`, headers),
        axios.get(`${API_URL}/collections/arrears`, headers),
        axios.get(`${API_URL}/reports/disbursements?period=month`, headers),
        axios.get(`${API_URL}/reports/collections?period=month`, headers)
      ]);
      
      if (results[0].status === 'fulfilled') setMetrics(results[0].value.data);
      if (results[1].status === 'fulfilled') setLoans(results[1].value.data);
      if (results[2].status === 'fulfilled') setBorrowers(results[2].value.data);
      if (results[3].status === 'fulfilled') setProducts(results[3].value.data);
      if (results[4].status === 'fulfilled') setPayments(results[4].value.data);
      if (results[5].status === 'fulfilled') setStaff(results[5].value.data);
      if (results[6].status === 'fulfilled') setArrearsLoans(results[6].value.data);
      if (results[7].status === 'fulfilled') setDisbursementsReport(results[7].value.data);
      if (results[8].status === 'fulfilled') setCollectionsReport(results[8].value.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeader, loanStatusFilter]);

  const fetchSiteStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/track/stats`, getAuthHeader());
      setSiteStats(res.data);
    } catch { /* ignore */ }
  }, [getAuthHeader]);

  const fetchCustomerApps = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);
      if (filterSearch) params.append('search', filterSearch);
      if (filterDateFrom) params.append('date_from', filterDateFrom);
      if (filterDateTo) params.append('date_to', filterDateTo);
      if (filterAmountMin) params.append('amount_min', filterAmountMin);
      if (filterAmountMax) params.append('amount_max', filterAmountMax);
      if (filterDebiCheck && filterDebiCheck !== 'all') params.append('debicheck', filterDebiCheck);
      if (filterPaidOut && filterPaidOut !== 'all') params.append('paid_out', filterPaidOut);
      const res = await axios.get(`${API_URL}/admin/customer-applications?${params.toString()}`, getAuthHeader());
      setCustomerApps(res.data);
    } catch { /* ignore */ }
  }, [getAuthHeader, filterStatus, filterSearch, filterDateFrom, filterDateTo, filterAmountMin, filterAmountMax, filterDebiCheck, filterPaidOut]);

  const togglePaidOut = async (app, checked) => {
    if (!checked && !window.confirm(`Unmark payout for #${app.application_number}?`)) return;
    try {
      const res = await axios.post(`${API_URL}/admin/customer-applications/${app.id}/payout`, { paid_out: checked }, getAuthHeader());
      toast.success(checked ? `Marked paid out on ${res.data.payout_date}` : 'Payout unmarked');
      fetchCustomerApps();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update payout'); }
  };

  const fetchDashStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/dashboard-stats`, getAuthHeader());
      setDashStats(res.data);
    } catch { /* ignore */ }
  }, [getAuthHeader]);

  useEffect(() => { fetchData(); fetchSeo(); fetchSiteStats(); fetchApplyLink(); fetchCustomerApps(); fetchDashStats(); }, [fetchData, fetchSeo, fetchSiteStats, fetchCustomerApps, fetchDashStats]);

  // Poll unread replies count
  useEffect(() => {
    if (!hasModule('communications')) return;
    const fetchUnread = async () => {
      try {
        const [msgRes, chatRes] = await Promise.allSettled([
          axios.get(`${API_URL}/admin/messaging/unread-count`, getAuthHeader()),
          axios.get(`${API_URL}/admin/chat/unread-count`, getAuthHeader()),
        ]);
        const msgCount = msgRes.status === 'fulfilled' ? (msgRes.value.data.unread || 0) : 0;
        const chatCount = chatRes.status === 'fulfilled' ? (chatRes.value.data.count || 0) : 0;
        setUnreadReplies(msgCount + chatCount);
      } catch { /* ignore */ }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, []);

  // Re-fetch when filters change (debounced for search)
  useEffect(() => {
    const timer = setTimeout(() => fetchCustomerApps(), filterSearch ? 400 : 0);
    return () => clearTimeout(timer);
  }, [filterStatus, filterDateFrom, filterDateTo, filterAmountMin, filterAmountMax, filterSearch, filterDebiCheck, filterPaidOut]);

  const fetchApplyLink = async () => {
    try {
      const res = await axios.get(`${API_URL}/site-settings`, getAuthHeader());
      setApplyLink(res.data.apply_link || '');
    } catch { /* ignore */ }
  };

  const saveApplyLink = async () => {
    setApplyLinkSaving(true);
    try {
      await axios.put(`${API_URL}/site-settings`, { apply_link: applyLink }, getAuthHeader());
      toast.success('Apply link saved');
    } catch { toast.error('Failed to save'); }
    finally { setApplyLinkSaving(false); }
  };

  const fetchUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (userRoleFilter && userRoleFilter !== 'all') params.set('role', userRoleFilter);
      if (userSearch) params.set('search', userSearch);
      const res = await axios.get(`${API_URL}/admin/users?${params.toString()}`, getAuthHeader());
      setAllUsers(res.data);
    } catch { /* ignore */ }
  }, [getAuthHeader, userRoleFilter, userSearch]);

  useEffect(() => {
    if (activeTab === 'users') {
      const t = setTimeout(() => fetchUsers(), userSearch ? 400 : 0);
      if (user?.role === 'admin' && !masterPwStatus) fetchMasterPwStatus();
      return () => clearTimeout(t);
    }
  }, [activeTab, fetchUsers, userSearch, userRoleFilter]);

  const handleChangeRole = async (userId, newRole) => {
    try {
      await axios.put(`${API_URL}/admin/users/${userId}/role`, { role: newRole }, getAuthHeader());
      toast.success('Role updated');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to change role'); }
  };

  const handleToggleStatus = async (userId, currentlyActive) => {
    try {
      await axios.put(`${API_URL}/admin/users/${userId}/status`, { is_active: !currentlyActive }, getAuthHeader());
      toast.success(currentlyActive ? 'User disabled' : 'User enabled');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleSaveUserProfile = async () => {
    const u = editUserDialog.user;
    if (!u) return;
    try {
      await axios.put(`${API_URL}/admin/users/${u.id}/profile`, editUserForm, getAuthHeader());
      toast.success('Profile updated');
      setEditUserDialog({ open: false, user: null });
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const fetchMasterPwStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/settings/master-password`, getAuthHeader());
      setMasterPwStatus(res.data);
    } catch (e) { /* non-admin staff: hidden */ }
  };

  const handleSaveMasterPw = async () => {
    setSavingMasterPw(true);
    try {
      await axios.put(`${API_URL}/admin/settings/master-password`, { new_password: masterPwValue }, getAuthHeader());
      toast.success('Master password saved');
      setMasterPwValue('');
      fetchMasterPwStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save master password'); }
    setSavingMasterPw(false);
  };

  const handleDisableMasterPw = async () => {
    if (!window.confirm('Disable the master password? You will no longer be able to log into client accounts with it.')) return;
    try {
      await axios.delete(`${API_URL}/admin/settings/master-password`, getAuthHeader());
      toast.success('Master password disabled');
      fetchMasterPwStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to disable'); }
  };

  const handleImpersonate = async (u) => {
    try {
      const res = await axios.post(`${API_URL}/admin/users/${u.id}/impersonate`, {}, getAuthHeader());
      impersonate(res.data.access_token, res.data.user);
      window.location.href = '/client/dashboard';
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not log in as client'); }
  };

  const handleGenerateAndSend = async (sendSms) => {
    const u = resetPwDialog.user;
    if (!u) return;
    setGeneratingPw(true);
    try {
      const res = await axios.post(`${API_URL}/admin/users/${u.id}/reset-and-send`,
        { send_message: sendSms, channel: 'sms' }, getAuthHeader());
      setTempPwResult(res.data);
      if (res.data.sent) toast.success(`Temporary password SMS'd to the client`);
      else if (sendSms && res.data.error) toast.error(res.data.error);
      else toast.success('Temporary password generated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate password'); }
    setGeneratingPw(false);
  };

  const handleResetPassword = async () => {
    const u = resetPwDialog.user;
    if (!u || !resetPwValue) return;
    try {
      await axios.put(`${API_URL}/admin/users/${u.id}/reset-password`, { new_password: resetPwValue }, getAuthHeader());
      toast.success('Password reset');
      setResetPwDialog({ open: false, user: null });
      setResetPwValue('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Delete user "${userName}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`, getAuthHeader());
      toast.success(`User "${userName}" deleted`);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const fetchRoles = useCallback(async () => {
    try {
      const [rolesRes, modsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/roles`, getAuthHeader()),
        axios.get(`${API_URL}/admin/modules`, getAuthHeader()),
      ]);
      setAllRoles(rolesRes.data);
      setAvailableModules(modsRes.data);
    } catch { /* ignore */ }
  }, [getAuthHeader]);

  useEffect(() => {
    if (activeTab === 'users') fetchRoles();
  }, [activeTab, fetchRoles]);

  const handleSaveRole = async () => {
    try {
      if (createRoleMode) {
        await axios.post(`${API_URL}/admin/roles`, roleForm, getAuthHeader());
        toast.success('Role created');
      } else if (editRoleDialog.role) {
        await axios.put(`${API_URL}/admin/roles/${editRoleDialog.role.id}`, { label: roleForm.label, modules: roleForm.modules }, getAuthHeader());
        toast.success('Role updated');
      }
      setEditRoleDialog({ open: false, role: null });
      setCreateRoleMode(false);
      fetchRoles();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Delete this role?')) return;
    try {
      await axios.delete(`${API_URL}/admin/roles/${roleId}`, getAuthHeader());
      toast.success('Role deleted');
      fetchRoles();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleLogout = () => { logout(); navigate('/'); };

  // API Actions
  const updateLoanStatus = async (loanId, newStatus, notes = '', reason = '') => {
    try {
      await axios.put(`${API_URL}/loans/${loanId}/status`, 
        { status: newStatus, notes, reason: reason || undefined }, 
        getAuthHeader()
      );
      toast.success(`Status updated to ${newStatus}`);
      setStatusDialog({ open: false, loan: null, newStatus: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const submitLoan = async (loanId) => {
    try {
      await axios.put(`${API_URL}/loans/${loanId}/submit`, {}, getAuthHeader());
      toast.success('Application submitted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    }
  };

  const handleDisbursement = async () => {
    if (!disbursementDialog.loan) return;
    try {
      await axios.post(`${API_URL}/disbursements`, {
        loan_id: disbursementDialog.loan.id,
        amount: parseFloat(disbursementAmount),
        disbursement_method: 'EFT'
      }, getAuthHeader());
      toast.success('Loan disbursed');
      setDisbursementDialog({ open: false, loan: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disburse');
    }
  };

  const handlePayment = async () => {
    if (!paymentDialog.loan) return;
    try {
      await axios.post(`${API_URL}/payments`, {
        loan_id: paymentDialog.loan.id,
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.method,
        reference: paymentForm.reference
      }, getAuthHeader());
      toast.success('Payment recorded');
      setPaymentDialog({ open: false, loan: null });
      setPaymentForm({ amount: '', method: 'EFT', reference: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleAddNote = async () => {
    if (!noteDialog.loan) return;
    try {
      await axios.post(`${API_URL}/loans/${noteDialog.loan.id}/notes`, 
        { note: noteText, note_type: noteType }, 
        getAuthHeader()
      );
      toast.success('Note added');
      setNoteDialog({ open: false, loan: null });
      setNoteText('');
      fetchData();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handlePTP = async () => {
    if (!ptpDialog.loan) return;
    try {
      await axios.post(`${API_URL}/collections/promise-to-pay`, {
        loan_id: ptpDialog.loan.id,
        promised_amount: parseFloat(ptpForm.amount),
        promised_date: ptpForm.date,
        notes: ptpForm.notes
      }, getAuthHeader());
      toast.success('Promise to Pay recorded');
      setPtpDialog({ open: false, loan: null });
      setPtpForm({ amount: '', date: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to record PTP');
    }
  };

  const handleCreateProduct = async () => {
    try {
      await axios.post(`${API_URL}/products`, { ...newProduct, is_active: true }, getAuthHeader());
      toast.success('Product created');
      setProductDialog({ open: false });
      fetchData();
    } catch (error) {
      toast.error('Failed to create product');
    }
  };

  const handleCreateStaff = async () => {
    try {
      await axios.post(`${API_URL}/staff`, newStaff, getAuthHeader());
      toast.success('Staff member created');
      setStaffDialog({ open: false });
      setNewStaff({ email: '', password: '', full_name: '', phone: '', role: 'loan_officer' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create staff');
    }
  };

  const processArrears = async () => {
    try {
      const res = await axios.post(`${API_URL}/admin/process-arrears`, {}, getAuthHeader());
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to process arrears');
    }
  };

  const exportData = (type) => {
    window.open(`${API_URL}/export/${type}`, '_blank');
  };

  // Helpers
  const formatCurrency = (amt) => new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amt || 0);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

  const getStatusBadge = (status) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    const Icon = cfg.icon;
    return <Badge className={`${cfg.color} flex items-center gap-1`}><Icon className="w-3 h-3" />{cfg.label}</Badge>;
  };

  const getArrearsBadge = (status) => {
    const cfg = ARREARS_CONFIG[status] || ARREARS_CONFIG.current;
    return <Badge className={`${cfg.color} text-white`}>{cfg.label}</Badge>;
  };

  const filteredLoans = loans.filter(l => {
    if (!searchQuery) return true;
    const s = searchQuery.toLowerCase();
    return l.borrower_name?.toLowerCase().includes(s) || 
           l.application_number?.toLowerCase().includes(s) ||
           l.borrower_email?.toLowerCase().includes(s);
  });

  if (isLoading && !metrics) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center"><div className="spinner mx-auto mb-4"></div><p className="text-slate-500">Loading...</p></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-emerald-900 text-white sticky top-0 z-50 shadow-lg">
        <div className="max-w-[1800px] mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <Banknote className="w-8 h-8" />
              <span className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>2k2day LMS</span>
              <Badge className="bg-white/20 text-xs">v3.0</Badge>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-emerald-200 text-sm hidden sm:inline">{user?.full_name}</span>
              <Badge className={ROLE_COLORS[user?.role] || 'bg-slate-100'}>{user?.role?.replace('_', ' ')}</Badge>
              {hasModule('communications') && (
                <button
                  className="relative p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                  onClick={() => { setActiveTab('communications'); }}
                  data-testid="header-inbox-btn"
                >
                  <MessageSquare className="w-5 h-5 text-emerald-200" />
                  {unreadReplies > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] flex items-center justify-center rounded-full bg-amber-500 text-white text-[9px] font-bold px-0.5 animate-pulse" data-testid="header-unread-badge">{unreadReplies}</span>
                  )}
                </button>
              )}
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white/70 hover:text-white">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-[1800px] mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <TabsList className="bg-white shadow-sm flex-wrap">
              {hasModule('overview') && <TabsTrigger value="overview" data-testid="overview-tab"><PieChart className="w-4 h-4 mr-1" />Overview</TabsTrigger>}
              {hasModule('origination') && <TabsTrigger value="origination" data-testid="origination-tab"><FileText className="w-4 h-4 mr-1" />Origination</TabsTrigger>}
              {hasModule('origination') && <TabsTrigger value="servicing" data-testid="servicing-tab"><Activity className="w-4 h-4 mr-1" />Servicing</TabsTrigger>}
              {hasModule('origination') && <TabsTrigger value="collections" data-testid="collections-tab"><AlertTriangle className="w-4 h-4 mr-1" />Collections</TabsTrigger>}
              {hasModule('origination') && <TabsTrigger value="borrowers" data-testid="borrowers-tab"><Users className="w-4 h-4 mr-1" />Borrowers</TabsTrigger>}
              {hasModule('origination') && <TabsTrigger value="reports" data-testid="reports-tab"><BarChart3 className="w-4 h-4 mr-1" />Reports</TabsTrigger>}
              {hasModule('bank_verification') && <TabsTrigger value="bank-verification" data-testid="bank-verify-tab"><ShieldCheck className="w-4 h-4 mr-1" />Bank Verify</TabsTrigger>}
              {hasModule('applications') && <TabsTrigger value="customer-apps" data-testid="applications-tab"><UserPlus className="w-4 h-4 mr-1" />Applications</TabsTrigger>}
              {hasModule('communications') && (
                <TabsTrigger value="communications" data-testid="communications-tab" className="relative">
                  <MessageSquare className="w-4 h-4 mr-1" />Comms
                  {unreadReplies > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-bold px-1 animate-pulse" data-testid="comms-unread-badge">{unreadReplies}</span>
                  )}
                </TabsTrigger>
              )}
              {hasModule('users') && <TabsTrigger value="users" data-testid="users-tab"><Users className="w-4 h-4 mr-1" />Users</TabsTrigger>}
              {hasModule('agents') && <TabsTrigger value="agents" data-testid="agents-tab"><Briefcase className="w-4 h-4 mr-1" />Agents</TabsTrigger>}
              {hasModule('settings') && <TabsTrigger value="settings" data-testid="settings-tab"><Settings className="w-4 h-4 mr-1" />Settings</TabsTrigger>}
              {hasModule('audit') && <TabsTrigger value="activity-log" data-testid="activity-log-tab"><Activity className="w-4 h-4 mr-1" />Audit</TabsTrigger>}
              {user?.role === 'admin' && <TabsTrigger value="access" data-testid="access-tab"><Lock className="w-4 h-4 mr-1" />Access</TabsTrigger>}
            </TabsList>
            <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
          </div>

          {/* ==================== OVERVIEW ==================== */}
          <TabsContent value="overview" className="space-y-6">
            {/* Instant Pre-Approval quick tool */}
            <div className="flex justify-end">
              <Button variant="outline" size="sm" className="border-emerald-700 text-emerald-800 hover:bg-emerald-50" data-testid="admin-preapproval-btn"
                onClick={() => setPreApprovalOpen(true)}>
                <Zap className="w-4 h-4 mr-1" /> Instant Pre-Approval
              </Button>
            </div>
            <Dialog open={preApprovalOpen} onOpenChange={setPreApprovalOpen}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Instant Pre-Approval Check</DialogTitle></DialogHeader>
                <PreApprovalCheck showSettings={user?.role === 'admin'} showApplyCta={false} getAuthHeader={getAuthHeader} />
              </DialogContent>
            </Dialog>
            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {hasModule('financials') && (<>
              <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0">
                <CardContent className="pt-4 pb-4">
                  <p className="text-emerald-100 text-xs uppercase">Portfolio</p>
                  <p className="text-2xl font-bold tabular-nums">{formatCurrency(metrics?.total_portfolio_value)}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
                <CardContent className="pt-4 pb-4">
                  <p className="text-blue-100 text-xs uppercase">Disbursed</p>
                  <p className="text-2xl font-bold tabular-nums">{formatCurrency(metrics?.total_disbursed)}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
                <CardContent className="pt-4 pb-4">
                  <p className="text-green-100 text-xs uppercase">Collected</p>
                  <p className="text-2xl font-bold tabular-nums">{formatCurrency(metrics?.total_collected)}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-0">
                <CardContent className="pt-4 pb-4">
                  <p className="text-amber-100 text-xs uppercase">Outstanding</p>
                  <p className="text-2xl font-bold tabular-nums">{formatCurrency(metrics?.total_outstanding)}</p>
                </CardContent>
              </Card>
              </>)}
              <Card>
                <CardContent className="pt-4 pb-4">
                  <p className="text-slate-500 text-xs uppercase">Active Loans</p>
                  <p className="text-2xl font-bold">{metrics?.active_loans_count || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <p className="text-slate-500 text-xs uppercase">Pending Apps</p>
                  <p className="text-2xl font-bold text-amber-600">{metrics?.pending_applications || 0}</p>
                </CardContent>
              </Card>
            </div>

            {/* Shareable Customer Link */}
            <Card className="border-l-4 border-l-emerald-500" data-testid="share-link-card">
              <CardContent className="pt-4 pb-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Link2 className="w-5 h-5 text-emerald-600 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">Customer Apply Link</p>
                      <p className="text-xs text-muted-foreground">Share this link with customers to apply for a loan</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 min-w-0">
                    <code className="text-xs bg-muted px-3 py-1.5 rounded select-all truncate max-w-[180px] sm:max-w-none" data-testid="share-link-url">
                      https://www.2k2day.co.za
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="copy-link-btn"
                      onClick={() => {
                        const url = 'https://www.2k2day.co.za';
                        if (navigator.clipboard?.writeText) {
                          navigator.clipboard.writeText(url).then(() => {
                            setLinkCopied(true);
                            toast.success('Link copied!');
                            setTimeout(() => setLinkCopied(false), 2000);
                          }).catch(() => {
                            // Fallback
                            const ta = document.createElement('textarea');
                            ta.value = url;
                            document.body.appendChild(ta);
                            ta.select();
                            document.execCommand('copy');
                            document.body.removeChild(ta);
                            setLinkCopied(true);
                            toast.success('Link copied!');
                            setTimeout(() => setLinkCopied(false), 2000);
                          });
                        }
                      }}
                    >
                      {linkCopied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                      <span className="ml-1 text-xs">{linkCopied ? 'Copied' : 'Copy'}</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                      data-testid="whatsapp-share-btn"
                      onClick={() => {
                        const msg = encodeURIComponent('Apply for a quick micro-loan today! 💰\nhttps://www.2k2day.co.za');
                        window.open(`https://wa.me/?text=${msg}`, '_blank');
                      }}
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      <span className="ml-1 text-xs">WhatsApp</span>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Site Traffic (grouped) */}
            <Card data-testid="site-traffic-card">
              <CardContent className="pt-4 pb-4">
                <p className="text-slate-500 text-xs uppercase font-semibold mb-3">Website Traffic</p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:divide-x divide-slate-100">
                  <div className="flex items-center gap-3">
                    <Eye className="w-6 h-6 text-indigo-300 shrink-0" />
                    <div>
                      <p className="text-2xl font-bold tabular-nums leading-tight" data-testid="admin-visit-count">{siteStats?.total_visits ?? 0}</p>
                      <p className="text-[11px] text-slate-400">Page visits · today {siteStats?.daily?.[siteStats.daily.length - 1]?.visits ?? 0}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 md:pl-4">
                    <ArrowUpRight className="w-6 h-6 text-emerald-300 shrink-0" />
                    <div>
                      <p className="text-2xl font-bold tabular-nums text-emerald-700 leading-tight" data-testid="admin-apply-count">{siteStats?.total_apply_clicks ?? 0}</p>
                      <p className="text-[11px] text-slate-400">Apply clicks · today {siteStats?.daily?.[siteStats.daily.length - 1]?.apply_clicks ?? 0}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 md:pl-4">
                    <Zap className="w-6 h-6 text-cyan-400 shrink-0" />
                    <div>
                      <p className="text-2xl font-bold tabular-nums text-cyan-700 leading-tight" data-testid="admin-preapproval-count">{siteStats?.total_preapproval_checks ?? 0}</p>
                      <p className="text-[11px] text-slate-400">Pre-approval checks · today {siteStats?.daily?.[siteStats.daily.length - 1]?.preapproval_checks ?? 0}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 md:pl-4">
                    <ChevronRight className="w-6 h-6 text-purple-300 shrink-0" />
                    <div>
                      <p className="text-2xl font-bold tabular-nums text-purple-700 leading-tight" data-testid="admin-preapproval-continue-count">{siteStats?.total_preapproval_continues ?? 0}</p>
                      <p className="text-[11px] text-slate-400">
                        Continued to apply
                        {siteStats?.total_preapproval_checks ? ` · ${Math.round((siteStats.total_preapproval_continues / siteStats.total_preapproval_checks) * 100)}%` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 md:pl-4">
                    <TrendingUp className="w-6 h-6 text-amber-300 shrink-0" />
                    <div>
                      <p className="text-2xl font-bold tabular-nums text-amber-600 leading-tight" data-testid="admin-conversion-rate">
                        {siteStats?.total_visits ? Math.round((siteStats.total_apply_clicks / siteStats.total_visits) * 100) : 0}%
                      </p>
                      <p className="text-[11px] text-slate-400">Visit → apply conversion</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Loan Lifecycle Pipeline */}
            <PipelinePanel getAuthHeader={getAuthHeader} />

            {/* Pre-Approval Outcomes */}
            {(siteStats?.total_preapproval_checks ?? 0) > 0 && (
              <Card data-testid="preapproval-outcomes-card">
                <CardContent className="pt-4 pb-4">
                  <p className="text-slate-500 text-xs uppercase font-semibold mb-3">Pre-Approval Outcomes</p>
                  <div className="flex flex-col sm:flex-row items-center gap-6">
                    <div className="w-40 h-40 shrink-0">
                      <ResponsiveContainer width="100%" height="100%">
                        <RPieChart>
                          <Pie
                            data={[
                              { name: 'Approved', value: siteStats?.preapproval_results?.approved ?? 0 },
                              { name: 'Needs Assessment', value: siteStats?.preapproval_results?.possible ?? 0 },
                              { name: 'Declined', value: siteStats?.preapproval_results?.declined ?? 0 },
                            ].filter(d => d.value > 0)}
                            dataKey="value" nameKey="name" innerRadius={40} outerRadius={65} paddingAngle={3}>
                            <Cell fill="#059669" />
                            <Cell fill="#d97706" />
                            <Cell fill="#dc2626" />
                          </Pie>
                          <Tooltip />
                        </RPieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="grid grid-cols-3 gap-6 flex-1 w-full">
                      {[
                        ['Approved', siteStats?.preapproval_results?.approved ?? 0, 'text-emerald-700', 'bg-emerald-500'],
                        ['Needs Assessment', siteStats?.preapproval_results?.possible ?? 0, 'text-amber-700', 'bg-amber-500'],
                        ['Declined', siteStats?.preapproval_results?.declined ?? 0, 'text-red-700', 'bg-red-500'],
                      ].map(([label, val, txt, dot]) => {
                        const total = (siteStats?.preapproval_results?.approved ?? 0) + (siteStats?.preapproval_results?.possible ?? 0) + (siteStats?.preapproval_results?.declined ?? 0);
                        return (
                          <div key={label} data-testid={`preapproval-outcome-${label.toLowerCase().replace(' ', '-')}`}>
                            <div className="flex items-center gap-1.5">
                              <span className={`w-2 h-2 rounded-full ${dot}`}></span>
                              <p className="text-[11px] text-slate-500">{label}</p>
                            </div>
                            <p className={`text-2xl font-bold tabular-nums ${txt}`}>{val}</p>
                            <p className="text-[11px] text-slate-400">{total ? Math.round((val / total) * 100) : 0}% of checks</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-2">Tune the rules via Instant Pre-Approval → Edit approval settings if too many visitors are declined.</p>
                  {(siteStats?.preapproval_reasons?.filter(r => r.reason !== 'Approved').length ?? 0) > 0 && (
                    <div className="mt-4 border-t border-slate-100 pt-3" data-testid="preapproval-reasons">
                      <p className="text-slate-500 text-xs uppercase font-semibold mb-2">What stops most visitors</p>
                      <div className="space-y-1.5">
                        {(() => {
                          const reasons = siteStats.preapproval_reasons.filter(r => r.reason !== 'Approved');
                          const max = Math.max(...reasons.map(r => r.count), 1);
                          return reasons.slice(0, 8).map(r => (
                            <div key={r.reason} className="flex items-center gap-2" data-testid={`preapproval-reason-${r.reason.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                              <p className="text-xs text-slate-600 w-44 truncate shrink-0" title={r.reason}>{r.reason}</p>
                              <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden">
                                <div className="h-full bg-red-400 rounded-full" style={{ width: `${(r.count / max) * 100}%` }}></div>
                              </div>
                              <p className="text-xs font-bold text-slate-700 tabular-nums w-8 text-right shrink-0">{r.count}</p>
                            </div>
                          ));
                        })()}
                      </div>
                      {siteStats?.preapproval_answers && Object.keys(siteStats.preapproval_answers).length > 0 && (
                        <div className="mt-3">
                          <p className="text-slate-500 text-xs uppercase font-semibold mb-2">Question answers (Yes rate)</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                            {Object.entries(siteStats.preapproval_answers).map(([q, counts]) => {
                              const yes = counts.yes ?? 0;
                              const total = Object.values(counts).reduce((a, b) => a + b, 0);
                              if (!total) return null;
                              return (
                                <div key={q} className="flex items-center justify-between gap-2 text-xs">
                                  <span className="text-slate-500 truncate" title={q}>{q}</span>
                                  <span className="font-semibold text-slate-700 tabular-nums shrink-0">{Math.round((yes / total) * 100)}% yes · {total}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Performance */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Approval Rate</CardTitle></CardHeader>
                <CardContent>
                  <span className="text-3xl font-bold text-emerald-600">{metrics?.approval_rate || 0}%</span>
                  <Progress value={metrics?.approval_rate || 0} className="mt-2 h-2" />
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Collection Rate</CardTitle></CardHeader>
                <CardContent>
                  <span className="text-3xl font-bold text-blue-600">{metrics?.collection_rate || 0}%</span>
                  <Progress value={metrics?.collection_rate || 0} className="mt-2 h-2" />
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Default Rate</CardTitle></CardHeader>
                <CardContent>
                  <span className="text-3xl font-bold text-red-600">{metrics?.default_rate || 0}%</span>
                  <Progress value={metrics?.default_rate || 0} className="mt-2 h-2 [&>div]:bg-red-500" />
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Total PAR</CardTitle></CardHeader>
                <CardContent>
                  <span className="text-3xl font-bold text-orange-600">{metrics?.total_par || 0}%</span>
                  <Progress value={metrics?.total_par || 0} className="mt-2 h-2 [&>div]:bg-orange-500" />
                </CardContent>
              </Card>
            </div>

            {/* PAR Buckets */}
            {hasModule('financials') && (
            <Card>
              <CardHeader><CardTitle>Portfolio at Risk by Aging Bucket</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-6 gap-4">
                  {[
                    { label: 'Current', value: metrics?.total_outstanding - (metrics?.par_1_7 + metrics?.par_8_30 + metrics?.par_31_60 + metrics?.par_61_90 + metrics?.par_90_plus || 0), color: 'bg-emerald-500' },
                    { label: '1-7 Days', value: metrics?.par_1_7, color: 'bg-yellow-400' },
                    { label: '8-30 Days', value: metrics?.par_8_30, color: 'bg-amber-500' },
                    { label: '31-60 Days', value: metrics?.par_31_60, color: 'bg-orange-500' },
                    { label: '61-90 Days', value: metrics?.par_61_90, color: 'bg-red-400' },
                    { label: '90+ Days', value: metrics?.par_90_plus, color: 'bg-red-600' }
                  ].map((b, i) => (
                    <div key={i} className="text-center p-4 rounded-xl bg-slate-50">
                      <div className={`w-4 h-4 ${b.color} rounded-full mx-auto mb-2`}></div>
                      <p className="text-xs text-slate-500">{b.label}</p>
                      <p className="text-lg font-bold">{formatCurrency(b.value)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            )}
          </TabsContent>

          {/* ==================== ORIGINATION (LOS) ==================== */}
          <TabsContent value="origination" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <CardTitle>Loan Applications</CardTitle>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 w-[200px]" />
                    </div>
                    <Select value={loanStatusFilter} onValueChange={setLoanStatusFilter}>
                      <SelectTrigger className="w-[160px]"><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="submitted">Submitted</SelectItem>
                        <SelectItem value="checks_pending">Checks Pending</SelectItem>
                        <SelectItem value="pending_decision">Pending Decision</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="declined">Declined</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={() => exportData('loans')}><Download className="w-4 h-4" /></Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">App #</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Date</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Borrower</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Amount</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Total</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Status</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLoans.filter(l => ['draft', 'submitted', 'checks_pending', 'checks_complete', 'pending_decision', 'approved', 'declined', 'contract_sent', 'contract_signed', 'pending_disbursement'].includes(l.status)).map((loan) => (
                        <tr key={loan.id} className="border-b border-slate-50 hover:bg-slate-50">
                          <td className="py-3 px-4 text-sm font-mono">{loan.application_number}</td>
                          <td className="py-3 px-4 text-sm">{formatDate(loan.created_at)}</td>
                          <td className="py-3 px-4">
                            <p className="font-medium">{loan.borrower_name}</p>
                            <p className="text-xs text-slate-500">{loan.borrower_email}</p>
                          </td>
                          <td className="py-3 px-4 tabular-nums">{formatCurrency(loan.loan_amount)}</td>
                          <td className="py-3 px-4 font-semibold text-emerald-700 tabular-nums">{formatCurrency(loan.total_amount)}</td>
                          <td className="py-3 px-4">{getStatusBadge(loan.status)}</td>
                          <td className="py-3 px-4">
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => setLoanDetailDialog({ open: true, loan })}><Eye className="w-4 h-4" /></Button>
                              {loan.status === 'draft' && (
                                <Button size="sm" variant="ghost" className="text-blue-600" onClick={() => submitLoan(loan.id)}><Send className="w-4 h-4" /></Button>
                              )}
                              {['submitted', 'checks_pending'].includes(loan.status) && (
                                <Button size="sm" variant="ghost" className="text-cyan-600" onClick={() => updateLoanStatus(loan.id, 'checks_complete', 'Checks completed')}>
                                  <FileCheck className="w-4 h-4" />
                                </Button>
                              )}
                              {['checks_complete', 'pending_decision'].includes(loan.status) && (
                                <>
                                  <Button size="sm" variant="ghost" className="text-emerald-600" onClick={() => updateLoanStatus(loan.id, 'approved', 'Application approved')}>
                                    <CheckCircle2 className="w-4 h-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setStatusDialog({ open: true, loan, newStatus: 'declined' })}>
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                              {loan.status === 'approved' && (
                                <Button size="sm" variant="ghost" className="text-purple-600" onClick={() => { setDisbursementAmount(loan.loan_amount.toString()); setDisbursementDialog({ open: true, loan }); }}>
                                  <Send className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ==================== SERVICING (LMS) ==================== */}
          <TabsContent value="servicing" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Active Loans</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">App #</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Borrower</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Disbursed</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Outstanding</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Paid</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Arrears</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Due Date</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loans.filter(l => ['active', 'disbursed'].includes(l.status)).map((loan) => (
                        <tr key={loan.id} className="border-b border-slate-50 hover:bg-slate-50">
                          <td className="py-3 px-4 text-sm font-mono">{loan.application_number}</td>
                          <td className="py-3 px-4">
                            <p className="font-medium">{loan.borrower_name}</p>
                            <p className="text-xs text-slate-500">{loan.borrower_phone}</p>
                          </td>
                          <td className="py-3 px-4 tabular-nums">{formatCurrency(loan.amount_disbursed)}</td>
                          <td className="py-3 px-4 font-semibold text-red-600 tabular-nums">{formatCurrency(loan.amount_outstanding)}</td>
                          <td className="py-3 px-4 text-emerald-600 tabular-nums">{formatCurrency(loan.amount_paid)}</td>
                          <td className="py-3 px-4">
                            {loan.days_in_arrears > 0 ? (
                              <span className="text-red-600 font-medium">{loan.days_in_arrears} days</span>
                            ) : (
                              <span className="text-emerald-600">Current</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm">{formatDate(loan.maturity_date)}</td>
                          <td className="py-3 px-4">
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => setLoanDetailDialog({ open: true, loan })}><Eye className="w-4 h-4" /></Button>
                              <Button size="sm" variant="ghost" className="text-blue-600" onClick={() => setPaymentDialog({ open: true, loan })}>
                                <CreditCard className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setNoteDialog({ open: true, loan })}>
                                <MessageSquare className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ==================== COLLECTIONS ==================== */}
          <TabsContent value="collections" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-800">Collections Management</h2>
              <Button onClick={processArrears}><RefreshCw className="w-4 h-4 mr-2" />Process Arrears</Button>
            </div>

            <MandateCollections getAuthHeader={getAuthHeader} onLoansUpdated={fetchCustomerApps} showFinancials={hasModule('financials')} />
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-600" />Loans in Arrears ({arrearsLoans.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 text-xs">App #</th>
                        <th className="text-left py-3 px-4 text-xs">Borrower</th>
                        <th className="text-left py-3 px-4 text-xs">Outstanding</th>
                        <th className="text-left py-3 px-4 text-xs">Days Overdue</th>
                        <th className="text-left py-3 px-4 text-xs">Bucket</th>
                        <th className="text-left py-3 px-4 text-xs">Dunning</th>
                        <th className="text-left py-3 px-4 text-xs">Agent</th>
                        <th className="text-left py-3 px-4 text-xs">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {arrearsLoans.map((loan) => (
                        <tr key={loan.id} className="border-b border-slate-50 hover:bg-red-50/30">
                          <td className="py-3 px-4 text-sm font-mono">{loan.application_number}</td>
                          <td className="py-3 px-4">
                            <p className="font-medium">{loan.borrower_name}</p>
                            <p className="text-xs text-slate-500">{loan.borrower_phone}</p>
                          </td>
                          <td className="py-3 px-4 font-semibold text-red-600 tabular-nums">{formatCurrency(loan.amount_outstanding)}</td>
                          <td className="py-3 px-4 font-bold text-red-700">{loan.days_in_arrears}</td>
                          <td className="py-3 px-4">{getArrearsBadge(loan.arrears_status)}</td>
                          <td className="py-3 px-4"><Badge variant="outline">{loan.dunning_stage || 'None'}</Badge></td>
                          <td className="py-3 px-4 text-sm">{loan.collections_agent_name || '-'}</td>
                          <td className="py-3 px-4">
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => setLoanDetailDialog({ open: true, loan })}><Eye className="w-4 h-4" /></Button>
                              <Button size="sm" variant="ghost" className="text-amber-600" onClick={() => setPtpDialog({ open: true, loan })}><Phone className="w-4 h-4" /></Button>
                              <Button size="sm" variant="ghost" className="text-blue-600" onClick={() => setPaymentDialog({ open: true, loan })}><CreditCard className="w-4 h-4" /></Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ==================== BORROWERS ==================== */}
          <TabsContent value="borrowers" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Borrowers ({borrowers.length})</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => exportData('borrowers')}><Download className="w-4 h-4 mr-1" />Export</Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 text-xs">Name</th>
                        <th className="text-left py-3 px-4 text-xs">ID Number</th>
                        <th className="text-left py-3 px-4 text-xs">Contact</th>
                        <th className="text-left py-3 px-4 text-xs">KYC</th>
                        <th className="text-left py-3 px-4 text-xs">Loans</th>
                        <th className="text-left py-3 px-4 text-xs">Borrowed</th>
                        <th className="text-left py-3 px-4 text-xs">Repaid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {borrowers.map((b) => (
                        <tr key={b.id} className="border-b border-slate-50 hover:bg-slate-50">
                          <td className="py-3 px-4 font-medium">{b.full_name}</td>
                          <td className="py-3 px-4 text-sm">{b.id_number}</td>
                          <td className="py-3 px-4">
                            <p className="text-sm">{b.email}</p>
                            <p className="text-xs text-slate-500">{b.phone}</p>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={b.kyc_status === 'verified' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                              {b.kyc_status}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <span>{b.total_loans} total</span>
                            {b.active_loans > 0 && <Badge className="ml-2 bg-blue-100 text-blue-700">{b.active_loans} active</Badge>}
                          </td>
                          <td className="py-3 px-4 tabular-nums">{formatCurrency(b.total_borrowed)}</td>
                          <td className="py-3 px-4 text-emerald-600 tabular-nums">{formatCurrency(b.total_repaid)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ==================== REPORTS ==================== */}
          <TabsContent value="reports" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle>Disbursements by Month</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {disbursementsReport.slice(0, 6).map((d, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="font-medium">{d.period}</span>
                        <div className="text-right">
                          <p className="font-bold text-blue-600">{formatCurrency(d.amount)}</p>
                          <p className="text-xs text-slate-500">{d.count} loans</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle>Collections by Month</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {collectionsReport.slice(0, 6).map((c, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="font-medium">{c.period}</span>
                        <div className="text-right">
                          <p className="font-bold text-emerald-600">{formatCurrency(c.amount)}</p>
                          <p className="text-xs text-slate-500">{c.count} payments</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="flex gap-4">
              <Button onClick={() => exportData('loans')}><Download className="w-4 h-4 mr-2" />Export Loans</Button>
              <Button onClick={() => exportData('payments')}><Download className="w-4 h-4 mr-2" />Export Payments</Button>
              <Button onClick={() => exportData('borrowers')}><Download className="w-4 h-4 mr-2" />Export Borrowers</Button>
            </div>
          </TabsContent>

          {/* ==================== BANK VERIFICATION ==================== */}
          <TabsContent value="bank-verification" className="space-y-4">
            <BankVerification getAuthHeader={getAuthHeader} />
          </TabsContent>

          {/* ==================== CUSTOMER APPLICATIONS ==================== */}
          <TabsContent value="customer-apps" className="space-y-4">
            <DraftsPanel getAuthHeader={getAuthHeader} />
            {/* Dashboard Charts */}
            {dashStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-2">
                <Card data-testid="stat-total-apps"><CardContent className="pt-4 pb-3">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">Applications</p>
                  <p className="text-xl font-bold text-[#0f2b4a] mt-1">{dashStats.total_applications}</p>
                </CardContent></Card>
                <Card data-testid="stat-approval-rate"><CardContent className="pt-4 pb-3">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">Approval Rate</p>
                  <p className="text-xl font-bold text-emerald-600 mt-1">{dashStats.approval_rate}%</p>
                </CardContent></Card>
                <Card data-testid="stat-decline-rate"><CardContent className="pt-4 pb-3">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">Decline Rate</p>
                  <p className="text-xl font-bold text-red-500 mt-1">{dashStats.decline_rate || 0}%</p>
                </CardContent></Card>
                {hasModule('financials') && (<>
                <Card data-testid="stat-total-value"><CardContent className="pt-4 pb-3">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">Total Value</p>
                  <p className="text-xl font-bold text-[#0f2b4a] mt-1">R{Number(dashStats.total_amount || 0).toLocaleString()}</p>
                </CardContent></Card>
                <Card data-testid="stat-approved-value"><CardContent className="pt-4 pb-3">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">Approved Value</p>
                  <p className="text-xl font-bold text-emerald-700 mt-1">R{Number(dashStats.approved_amount || 0).toLocaleString()}</p>
                </CardContent></Card>
                <Card data-testid="stat-avg-loan"><CardContent className="pt-4 pb-3">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">Avg Loan</p>
                  <p className="text-xl font-bold text-blue-600 mt-1">R{Number(dashStats.avg_loan_amount || 0).toLocaleString()}</p>
                </CardContent></Card>
                </>)}
                <Card data-testid="stat-repeat-borrowers"><CardContent className="pt-4 pb-3">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">Repeat Clients</p>
                  <p className="text-xl font-bold text-purple-600 mt-1">{dashStats.repeat_borrowers}</p>
                </CardContent></Card>
                <Card data-testid="stat-total-clients"><CardContent className="pt-4 pb-3">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">Total Clients</p>
                  <p className="text-xl font-bold text-slate-700 mt-1">{dashStats.total_clients}</p>
                </CardContent></Card>
              </div>
            )}

            {/* SLA Dashboard */}
            {dashStats?.sla && (
              <Card className="border-l-4 border-l-[#0f2b4a]" data-testid="sla-dashboard">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4 text-[#0f2b4a]" />
                    <h3 className="font-semibold text-sm text-[#0f2b4a]">SLA Performance — Target: 24h Response</h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="text-center p-3 rounded-xl bg-slate-50">
                      <p className="text-[10px] text-slate-500 uppercase">Avg Response</p>
                      <p className="text-2xl font-bold text-[#0f2b4a]">{dashStats.sla.avg_hours < 1 ? `${Math.round(dashStats.sla.avg_hours * 60)}m` : `${dashStats.sla.avg_hours.toFixed(1)}h`}</p>
                    </div>
                    <div className="text-center p-3 rounded-xl bg-slate-50">
                      <p className="text-[10px] text-slate-500 uppercase">Overall Grade</p>
                      <p className={`text-3xl font-black ${
                        {'A+':'text-emerald-600','A':'text-green-600','B':'text-blue-600','C':'text-amber-600','D':'text-orange-600','F':'text-red-600'}[dashStats.sla.overall_grade] || 'text-slate-600'
                      }`}>{dashStats.sla.overall_grade}</p>
                    </div>
                    <div className="text-center p-3 rounded-xl bg-slate-50">
                      <p className="text-[10px] text-slate-500 uppercase">24h Compliance</p>
                      <p className={`text-2xl font-bold ${dashStats.sla.compliance_rate >= 80 ? 'text-emerald-600' : dashStats.sla.compliance_rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{dashStats.sla.compliance_rate}%</p>
                    </div>
                    <div className="text-center p-3 rounded-xl bg-slate-50">
                      <p className="text-[10px] text-slate-500 uppercase">Measured</p>
                      <p className="text-2xl font-bold text-slate-700">{dashStats.sla.within_24h}/{dashStats.sla.total_measured}</p>
                    </div>
                  </div>
                  {/* Grade Distribution */}
                  <div className="flex items-end gap-1.5 h-16" data-testid="sla-grade-chart">
                    {[
                      { grade: 'A+', color: 'bg-emerald-500', label: '<1.2h' },
                      { grade: 'A', color: 'bg-green-500', label: '1.2-2h' },
                      { grade: 'B', color: 'bg-blue-500', label: '2-5h' },
                      { grade: 'C', color: 'bg-amber-500', label: '5-12h' },
                      { grade: 'D', color: 'bg-orange-500', label: '12-24h' },
                      { grade: 'F', color: 'bg-red-500', label: '>24h' },
                    ].map(g => {
                      const count = dashStats.sla.grade_distribution?.[g.grade] || 0;
                      const max = Math.max(...Object.values(dashStats.sla.grade_distribution || {}), 1);
                      const pct = (count / max) * 100;
                      return (
                        <div key={g.grade} className="flex-1 flex flex-col items-center gap-1">
                          <span className="text-[10px] font-bold tabular-nums">{count}</span>
                          <div className={`w-full ${g.color} rounded-t`} style={{ height: `${Math.max(pct, 4)}%` }} title={`${g.grade}: ${count}`}></div>
                          <div className="text-center">
                            <span className="text-[10px] font-bold block">{g.grade}</span>
                            <span className="text-[8px] text-slate-400">{g.label}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Charts Row */}
            {dashStats && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Monthly Applications</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={dashStats.monthly_trends}>
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#0f2b4a" radius={[4,4,0,0]} name="Applications" />
                        <Bar dataKey="approved" fill="#059669" radius={[4,4,0,0]} name="Approved" />
                        <Bar dataKey="declined" fill="#dc2626" radius={[4,4,0,0]} name="Declined" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Status Breakdown</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <RPieChart>
                        <Pie data={Object.entries(dashStats.by_status || {}).map(([k, v]) => ({ name: k, value: v }))}
                          cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                          {Object.keys(dashStats.by_status || {}).map((_, i) => (
                            <Cell key={i} fill={['#3b82f6','#059669','#dc2626','#7c3aed','#f59e0b','#64748b'][i % 6]} />
                          ))}
                        </Pie>
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                      </RPieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Filters + Table */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div>
                  <CardTitle>Customer Loan Applications</CardTitle>
                  <CardDescription>{customerApps.length} application(s) found</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" data-testid="export-customer-csv"
                    onClick={async () => {
                      try {
                        const params = new URLSearchParams();
                        if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
                        if (filterSearch) params.set('search', filterSearch);
                        if (filterDateFrom) params.set('date_from', filterDateFrom);
                        if (filterDateTo) params.set('date_to', filterDateTo);
                        if (filterAmountMin) params.set('amount_min', filterAmountMin);
                        if (filterAmountMax) params.set('amount_max', filterAmountMax);
                        const qs = params.toString() ? `?${params.toString()}` : '';
                        const res = await axios.get(`${API_URL}/admin/customer-applications/export/csv${qs}`, {
                          ...getAuthHeader(),
                          responseType: 'blob'
                        });
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `2k2day_applications_${new Date().toISOString().slice(0,10)}.csv`;
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                        toast.success('CSV exported');
                      } catch { toast.error('Export failed'); }
                    }}>
                    <Download className="w-4 h-4 mr-1" />Export CSV
                  </Button>
                  <Button variant="outline" size="sm" data-testid="follow-up-btn"
                    className={filterStatus === 'awaiting_documents' ? 'bg-amber-100 border-amber-400 text-amber-800' : 'text-amber-700 border-amber-300 hover:bg-amber-50'}
                    onClick={() => setFilterStatus(filterStatus === 'awaiting_documents' ? 'all' : 'awaiting_documents')}>
                    <Clock className="w-4 h-4 mr-1" />
                    Follow up ({customerApps.filter(a => a.status === 'awaiting_documents').length})
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { fetchCustomerApps(); fetchDashStats(); }} data-testid="refresh-customer-apps">
                    <RefreshCw className="w-4 h-4 mr-1" />Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4 p-3 bg-slate-50 rounded-lg" data-testid="app-filters">
                  <Input placeholder="Search name, ref, email..." value={filterSearch} onChange={e => setFilterSearch(e.target.value)} className="h-9 text-sm" data-testid="filter-search" />
                  <Select value={filterStatus} onValueChange={v => { setFilterStatus(v); }}>
                    <SelectTrigger className="h-9 text-sm" data-testid="filter-status"><SelectValue placeholder="All Statuses" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="under_review">Under Review</SelectItem>
                      <SelectItem value="awaiting_documents">Awaiting Documents</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="declined">Declined</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                      <SelectItem value="repaid">Repaid</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} className="h-9 text-sm" placeholder="From" data-testid="filter-date-from" />
                  <Input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} className="h-9 text-sm" placeholder="To" data-testid="filter-date-to" />
                  <Input type="number" placeholder="Min R" value={filterAmountMin} onChange={e => setFilterAmountMin(e.target.value)} className="h-9 text-sm" data-testid="filter-amount-min" />
                  <Input type="number" placeholder="Max R" value={filterAmountMax} onChange={e => setFilterAmountMax(e.target.value)} className="h-9 text-sm" data-testid="filter-amount-max" />
                  <Select value={filterDebiCheck} onValueChange={setFilterDebiCheck}>
                    <SelectTrigger className="h-9 text-sm" data-testid="filter-debicheck"><SelectValue placeholder="DebiCheck" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All DebiCheck</SelectItem>
                      <SelectItem value="none">Not Submitted</SelectItem>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterPaidOut} onValueChange={setFilterPaidOut}>
                    <SelectTrigger className="h-9 text-sm" data-testid="filter-paid-out"><SelectValue placeholder="Paid Out" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Paid Out: All</SelectItem>
                      <SelectItem value="yes">Paid Out: Yes</SelectItem>
                      <SelectItem value="no">Paid Out: No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {customerApps.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No customer applications yet</p>
                ) : (
                  <>
                    {/* Bulk Action Bar */}
                    {selectedApps.length > 0 && (
                      <div className="flex items-center gap-3 p-3 mb-4 bg-[#0f2b4a] text-white rounded-lg" data-testid="bulk-action-bar">
                        <span className="text-sm font-medium">{selectedApps.length} selected</span>
                        <Select value={bulkStatus} onValueChange={setBulkStatus}>
                          <SelectTrigger className="w-[160px] h-8 bg-white/10 border-white/20 text-white text-sm" data-testid="bulk-status-select">
                            <SelectValue placeholder="Bulk action..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="under_review">Under Review</SelectItem>
                            <SelectItem value="awaiting_documents">Awaiting Documents</SelectItem>
                            <SelectItem value="approved">Approve</SelectItem>
                            <SelectItem value="declined">Decline</SelectItem>
                            <SelectItem value="cancelled">Cancel</SelectItem>
                            <SelectItem value="repaid">Mark Repaid</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="secondary" disabled={!bulkStatus || bulkLoading} data-testid="bulk-apply-btn"
                          onClick={async () => {
                            setBulkLoading(true);
                            try {
                              await axios.put(`${API_URL}/admin/customer-applications/bulk-status`,
                                { app_ids: selectedApps, status: bulkStatus },
                                getAuthHeader()
                              );
                              toast.success(`${selectedApps.length} application(s) updated to ${bulkStatus}`);
                              setSelectedApps([]);
                              setBulkStatus('');
                              fetchCustomerApps();
                            } catch { toast.error('Bulk update failed'); }
                            finally { setBulkLoading(false); }
                          }}>
                          {bulkLoading ? 'Updating...' : 'Apply'}
                        </Button>
                        <Button size="sm" variant="ghost" className="text-white/70 hover:text-white" onClick={() => setSelectedApps([])} data-testid="bulk-clear-btn">
                          Clear
                        </Button>
                      </div>
                    )}
                    <div className="overflow-x-auto">
                      <table className="data-table" data-testid="customer-apps-table">
                        <thead>
                          <tr>
                            <th className="w-10">
                              <input type="checkbox" className="rounded"
                                checked={selectedApps.length === customerApps.length && customerApps.length > 0}
                                onChange={e => setSelectedApps(e.target.checked ? customerApps.map(a => a.id) : [])}
                                data-testid="select-all-apps"
                              />
                            </th>
                            <th>Ref</th>
                            <th>Name</th>
                            <th>ID Number</th>
                            <th>Phone</th>
                            <th>Loan Amount</th>
                            <th>Total Repayment</th>
                            <th>Status</th>
                            <th>DebiCheck</th>
                            <th>Verify</th>
                            <th>Paid Out</th>
                            <th>Agent</th>
                            <th>SLA</th>
                            <th>Date</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {customerApps.map(app => (
                            <tr key={app.id}
                              className={selectedApps.includes(app.id) ? 'bg-blue-50'
                                : (app.verifynow_summary?.id === 'PASS' && app.verifynow_summary?.bank === 'PASS')
                                  ? 'bg-emerald-50/80 shadow-[inset_4px_0_0_0_#10b981]'
                                  : (app.verifynow_summary?.id === 'FAIL' || app.verifynow_summary?.bank === 'FAIL')
                                    ? 'bg-red-50/80 shadow-[inset_4px_0_0_0_#ef4444]'
                                    : ''}
                              data-testid={`app-row-${app.id}`}>
                              <td>
                                <input type="checkbox" className="rounded"
                                  checked={selectedApps.includes(app.id)}
                                  onChange={e => setSelectedApps(prev => e.target.checked ? [...prev, app.id] : prev.filter(id => id !== app.id))}
                                  data-testid={`select-app-${app.id}`}
                                />
                              </td>
                              <td className="font-mono text-xs">{app.application_number}</td>
                              <td>
                                <div className="flex items-center gap-1.5">
                                  <span className="font-medium">{app.full_name}</span>
                                  {app.is_repeat_borrower && (
                                    <LoanHistoryPopover app={app} getAuthHeader={getAuthHeader} />
                                  )}
                                </div>
                              </td>
                              <td className="text-xs text-slate-500">{app.id_number}</td>
                              <td className="text-sm">{app.cell_number}</td>
                              <td className="tabular-nums">
                                <button className="hover:underline decoration-dotted text-left" title="Set approved amount" data-testid={`approved-amount-btn-${app.id}`}
                                  onClick={(e) => { e.stopPropagation(); setApprovedAmtDialog({ open: true, app }); setApprovedAmtValue(String(app.approved_amount || app.loan_amount || '')); }}>
                                  R{(app.loan_amount || 0).toLocaleString()}
                                  {app.applied_amount && app.applied_amount !== app.loan_amount && (
                                    <span className="block text-[10px] text-slate-400">applied R{(app.applied_amount || 0).toLocaleString()}</span>
                                  )}
                                </button>
                              </td>
                              <td className="tabular-nums font-medium">R{(app.total_repayment || 0).toLocaleString()}</td>
                              <td>
                                <Badge variant="outline" className={
                                  app.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                                  app.status === 'repaid' ? 'bg-teal-100 text-teal-700' :
                                  app.status === 'declined' ? 'bg-red-100 text-red-700' :
                                  app.status === 'under_review' ? 'bg-purple-100 text-purple-700' :
                                  app.status === 'awaiting_documents' ? 'bg-amber-100 text-amber-700 border-amber-300' :
                                  'bg-blue-100 text-blue-700'
                                }>{app.status === 'awaiting_documents' ? 'awaiting docs' : app.status}</Badge>
                              </td>
                              <td>
                                <DebiCheckTag app={app} getAuthHeader={getAuthHeader} onUpdated={fetchCustomerApps} />
                              </td>
                              <td>
                                <div className="flex gap-0.5" data-testid={`verify-ticks-${app.id}`}>
                                  {[['ID', app.verifynow_summary?.id], ['Bk', app.verifynow_summary?.bank]].map(([lbl, st]) => (
                                    <span key={lbl} title={`${lbl === 'ID' ? 'ID verification' : 'Bank ownership'}: ${st || 'NOT_CHECKED'}`}
                                      className={`inline-flex items-center justify-center min-w-[24px] h-5 px-1 rounded text-[9px] font-bold ${
                                        st === 'PASS' ? 'bg-emerald-100 text-emerald-700' :
                                        st === 'FAIL' ? 'bg-red-100 text-red-700' :
                                        st === 'REVIEW' ? 'bg-amber-100 text-amber-800' :
                                        st === 'ERROR' ? 'bg-orange-100 text-orange-700' :
                                        st === 'PENDING' ? 'bg-blue-100 text-blue-700' :
                                        'bg-slate-100 text-slate-400'
                                      }`}>
                                      {lbl}{st === 'PASS' ? '✓' : st === 'FAIL' ? '✗' : st === 'REVIEW' || st === 'ERROR' ? '!' : ''}
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td>
                                <div className="flex flex-col items-center gap-0.5" title={app.paid_out ? `Paid out ${app.payout_date}${app.payout_by ? ` by ${app.payout_by}` : ''}` : 'Tick to mark as paid out (money disbursed)'}>
                                  <input type="checkbox" className="rounded accent-teal-600 w-4 h-4 cursor-pointer"
                                    checked={!!app.paid_out}
                                    onChange={e => togglePaidOut(app, e.target.checked)}
                                    data-testid={`paid-out-tick-${app.id}`}
                                  />
                                  {app.paid_out && app.payout_date && (
                                    <span className="text-[10px] text-teal-700 font-medium whitespace-nowrap">{app.payout_date}</span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <AgentCell app={app} getAuthHeader={getAuthHeader} onUpdated={fetchCustomerApps} />
                              </td>
                              <td>
                                {app.sla_grade && (
                                  <div className="flex items-center gap-1" data-testid={`sla-badge-${app.id}`}>
                                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-black ${
                                      app.sla_grade === 'A+' ? 'bg-emerald-100 text-emerald-700' :
                                      app.sla_grade === 'A' ? 'bg-green-100 text-green-700' :
                                      app.sla_grade === 'B' ? 'bg-blue-100 text-blue-700' :
                                      app.sla_grade === 'C' ? 'bg-amber-100 text-amber-700' :
                                      app.sla_grade === 'D' ? 'bg-orange-100 text-orange-700' :
                                      app.sla_grade === 'F' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'
                                    }`}>{app.sla_grade}</span>
                                    <span className="text-[10px] text-slate-400">{app.sla_hours != null ? (app.sla_hours < 1 ? `${Math.round(app.sla_hours * 60)}m` : `${app.sla_hours.toFixed(1)}h`) : ''}{app.sla_pending ? '*' : ''}</span>
                                  </div>
                                )}
                              </td>
                              <td className="text-xs text-slate-500">{new Date(app.created_at).toLocaleDateString('en-ZA')}</td>
                              <td>
                                <div className="flex items-center">
                                  <Button variant="ghost" size="sm" onClick={() => setCustomerAppDetail({ open: true, app })} data-testid={`view-app-${app.id}`}>
                                    <Eye className="w-4 h-4 mr-1" />View
                                  </Button>
                                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" data-testid={`delete-app-${app.id}`}
                                    onClick={async () => {
                                      if (!window.confirm(`Delete application #${app.application_number} (${app.first_name} ${app.last_name})? This cannot be undone.`)) return;
                                      try {
                                        await axios.delete(`${API_URL}/admin/customer-applications/${app.id}`, getAuthHeader());
                                        toast.success('Application deleted');
                                        fetchCustomerApps();
                                        fetchDashStats();
                                      } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
                                    }}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Application Detail Dialog */}
            <Dialog open={customerAppDetail.open} onOpenChange={open => !open && setCustomerAppDetail({ open: false, app: null })}>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                {customerAppDetail.app && (() => {
                  const a = customerAppDetail.app;
                  const docDownloadUrl = (fileId) => `${API_URL}/client/documents/${fileId}`;
                  const handleDocDownload = async (fileId, label) => {
                    try {
                      const res = await axios.get(docDownloadUrl(fileId), { ...getAuthHeader(), responseType: 'blob' });
                      const url = window.URL.createObjectURL(res.data);
                      const link = document.createElement('a');
                      link.href = url;
                      const ct = res.headers['content-type'] || '';
                      const ext = ct.includes('pdf') ? 'pdf' : ct.includes('png') ? 'png' : 'jpg';
                      link.download = `${a.application_number}_${label}.${ext}`;
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                    } catch { toast.error(`Failed to download ${label}`); }
                  };
                  return (
                    <>
                      <DialogHeader>
                        <DialogTitle className="flex items-center justify-between">
                          <span>Application {a.application_number}</span>
                          <Badge variant="outline" className={
                            a.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                            a.status === 'declined' ? 'bg-red-100 text-red-700' :
                            a.status === 'under_review' ? 'bg-purple-100 text-purple-700' :
                            'bg-blue-100 text-blue-700'
                          }>{a.status}</Badge>
                        </DialogTitle>
                      </DialogHeader>
                      {/* Quick Actions */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button size="sm" variant="outline" data-testid="download-pdf-btn"
                          onClick={async () => {
                            try {
                              const res = await axios.get(`${API_URL}/admin/customer-applications/${a.id}/pdf`, { ...getAuthHeader(), responseType: 'blob' });
                              const url = window.URL.createObjectURL(res.data);
                              const link = document.createElement('a');
                              link.href = url;
                              link.download = `2k2day_application_${a.application_number}.pdf`;
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                              window.URL.revokeObjectURL(url);
                            } catch { toast.error('PDF download failed'); }
                          }}>
                          <Download className="w-4 h-4 mr-1" />Download PDF
                        </Button>
                        {a.cell_number && (
                          <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid="whatsapp-notify-btn"
                            onClick={() => {
                              const phone = a.cell_number.replace(/^0/, '27').replace(/\s/g, '');
                              const statusText = a.status === 'approved' ? 'approved! We will contact you with next steps.'
                                : a.status === 'declined' ? 'not approved at this time. Please contact us for more info.'
                                : a.status === 'under_review' ? 'currently under review. We will notify you once a decision is made.'
                                : 'being processed.';
                              const msg = encodeURIComponent(`Hi ${a.first_name}, your 2k2day loan application (Ref: ${a.application_number}) for R${Number(a.loan_amount || 0).toLocaleString()} has been ${statusText}\n\nRegards,\n2k2day Team`);
                              window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
                            }}>
                            <MessageCircle className="w-4 h-4 mr-1" />WhatsApp
                          </Button>
                        )}
                      </div>
                      <div className="space-y-4 text-sm">

                        {/* Personal Info */}
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-1.5"><UserPlus className="w-4 h-4"/>Personal Details</h4>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            <DetailRow label="Full Name" value={`${a.title || ''} ${a.first_name} ${a.last_name}`} />
                            <DetailRow label="ID Number" value={a.id_number} />
                            <DetailRow label="Date of Birth" value={a.date_of_birth} />
                            <DetailRow label="Gender" value={a.gender} />
                            <DetailRow label="Marital Status" value={a.marital_status} />
                            <DetailRow label="Dependants" value={a.dependants} />
                          </div>
                        </div>

                        {/* Contact */}
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-1.5"><Phone className="w-4 h-4"/>Contact Details</h4>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            <DetailRow label="Cell Number" value={a.cell_number} />
                            <DetailRow label="Alt Number" value={a.alt_number} />
                            <DetailRow label="Email" value={a.email} />
                          </div>
                        </div>

                        {/* Address */}
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-1.5"><Target className="w-4 h-4"/>Residential Address</h4>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            <DetailRow label="Street Address" value={a.street_address} />
                            <DetailRow label="Suburb" value={a.suburb} />
                            <DetailRow label="City" value={a.city} />
                            <DetailRow label="Province" value={a.province} />
                            <DetailRow label="Postal Code" value={a.postal_code} />
                            <DetailRow label="Residential Status" value={a.residential_status} />
                            <DetailRow label="Years at Address" value={a.years_at_address} />
                          </div>
                        </div>

                        {/* Employment */}
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-1.5"><Briefcase className="w-4 h-4"/>Employment</h4>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            <DetailRow label="Status" value={a.employment_status} />
                            <DetailRow label="Employer" value={a.employer_name} />
                            <DetailRow label="Contact Person" value={a.employer_contact_person} />
                            <DetailRow label="Employer Phone" value={a.employer_phone} />
                            <DetailRow label="Employer Email" value={a.employer_email} />
                            <DetailRow label="Employer Address" value={[a.employer_address, a.employer_suburb, a.employer_city, a.employer_postal_code].filter(Boolean).join(', ')} />
                            <DetailRow label="Employer Phone" value={a.employer_phone} />
                            <DetailRow label="Occupation" value={a.occupation} />
                            <DetailRow label="Start Date" value={a.employment_date} />
                            <DetailRow label="Salary Date" value={a.salary_date} />
                          </div>
                        </div>

                        {/* Bank + Financial */}
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-1.5"><Wallet className="w-4 h-4"/>Bank & Financial</h4>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            <DetailRow label="Bank" value={a.bank_name} />
                            <DetailRow label="Account Type" value={a.account_type} />
                            <DetailRow label="Account Number" value={a.account_number} />
                            <DetailRow label="Branch Code" value={a.branch_code} />
                            <DetailRow label="Account Holder" value={a.account_holder} />
                            <DetailRow label="Gross Income" value={a.gross_income ? `R${Number(a.gross_income).toLocaleString()}` : null} />
                            <DetailRow label="Net Income" value={a.net_income ? `R${Number(a.net_income).toLocaleString()}` : null} />
                            <DetailRow label="Total Expenses" value={a.total_expenses ? `R${Number(a.total_expenses).toLocaleString()}` : null} />
                            <DetailRow label="Total Deductions" value={a.total_deductions ? `R${Number(a.total_deductions).toLocaleString()}` : null} />
                            <DetailRow label="Existing Loans" value={a.existing_loans} />
                          </div>
                        </div>

                        {/* Loan Details */}
                        <div className="p-4 bg-[#0f2b4a] text-white rounded-lg">
                          <h4 className="font-semibold mb-3 flex items-center gap-1.5"><Banknote className="w-4 h-4"/>Loan Details</h4>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            <div><span className="text-white/60 text-xs">Loan Amount</span><p className="font-bold text-lg">R{Number(a.loan_amount || 0).toLocaleString()}</p></div>
                            <div><span className="text-white/60 text-xs">Total Repayment</span><p className="font-bold text-lg text-emerald-300">R{Number(a.total_repayment || 0).toLocaleString()}</p></div>
                            <div><span className="text-white/60 text-xs">Period</span><p className="font-medium">{a.repayment_period || 30} days</p></div>
                            <div><span className="text-white/60 text-xs">Initiation Fee</span><p className="font-medium">R{Number(a.initiation_fee || 0).toLocaleString()}</p></div>
                            <div><span className="text-white/60 text-xs">Interest</span><p className="font-medium">R{Number(a.interest_amount || 0).toLocaleString()}</p></div>
                            <div><span className="text-white/60 text-xs">Admin Fee</span><p className="font-medium">R{Number(a.admin_fee || 0).toLocaleString()}</p></div>
                            <div><span className="text-white/60 text-xs">Insurance</span><p className="font-medium">R{Number(a.insurance || 0).toLocaleString()}</p></div>
                            <div><span className="text-white/60 text-xs">Purpose</span><p className="font-medium">{a.loan_purpose || '—'}</p></div>
                            <div><span className="text-white/60 text-xs">Debit Order Date</span><p className="font-medium">{a.debit_order_date || '—'}</p></div>
                          </div>
                        </div>

                        {/* Affordability Details */}
                        {a.affordability && Object.keys(a.affordability).length > 0 && (
                          <div className="p-4 bg-slate-50 rounded-lg" data-testid="admin-affordability-view">
                            <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-1.5"><DollarSign className="w-4 h-4"/>Loan Affordability Details</h4>
                            {[
                              { key: 'income', label: 'Income', border: 'border-emerald-300' },
                              { key: 'statutory_deductions', label: 'Statutory Deductions', border: 'border-blue-300' },
                              { key: 'expenses', label: 'Expenses', border: 'border-amber-300' },
                              { key: 'debt_obligations', label: 'Debt Obligations', border: 'border-red-300' },
                            ].map(sec => {
                              const catData = a.affordability[sec.key];
                              if (!catData || Object.keys(catData).length === 0) return null;
                              return (
                                <div key={sec.key} className={`mb-3 border-l-2 ${sec.border} pl-3`}>
                                  <p className="text-xs font-semibold text-slate-500 uppercase mb-1">{sec.label}</p>
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr className="border-b border-slate-200">
                                          <th className="text-left py-1 pr-2 text-slate-400">Item</th>
                                          <th className="text-right py-1 px-1 text-slate-400">Month 1</th>
                                          <th className="text-right py-1 px-1 text-slate-400">Month 2</th>
                                          <th className="text-right py-1 px-1 text-slate-400">Month 3</th>
                                          <th className="text-right py-1 px-1 text-slate-400 font-semibold">Final</th>
                                          <th className="text-left py-1 pl-2 text-slate-400">Notes</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {Object.entries(catData).map(([itemKey, vals]) => (
                                          <tr key={itemKey} className="border-b border-slate-100">
                                            <td className="py-1 pr-2 text-slate-700 capitalize">{itemKey.replace(/_/g, ' ')}</td>
                                            <td className="py-1 px-1 text-right tabular-nums">{vals.month1 || '—'}</td>
                                            <td className="py-1 px-1 text-right tabular-nums">{vals.month2 || '—'}</td>
                                            <td className="py-1 px-1 text-right tabular-nums">{vals.month3 || '—'}</td>
                                            <td className="py-1 px-1 text-right tabular-nums font-semibold">{vals.final || '—'}</td>
                                            <td className="py-1 pl-2 text-slate-500">{vals.notes || ''}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              );
                            })}
                            {/* Summary row */}
                            {(() => {
                              const sumFinal = (catKey) => {
                                const cat = a.affordability[catKey] || {};
                                return Object.values(cat).reduce((sum, r) => sum + (parseFloat(r?.final) || 0), 0);
                              };
                              const inc = sumFinal('income');
                              const ded = sumFinal('statutory_deductions');
                              const exp = sumFinal('expenses');
                              const debt = sumFinal('debt_obligations');
                              const disp = inc - ded - exp - debt;
                              return inc > 0 ? (
                                <div className="grid grid-cols-5 gap-2 mt-2 p-2 bg-white rounded border text-xs">
                                  <div><span className="text-slate-400">Income</span><p className="font-bold text-emerald-700">R{inc.toFixed(2)}</p></div>
                                  <div><span className="text-slate-400">Deductions</span><p className="font-medium">R{ded.toFixed(2)}</p></div>
                                  <div><span className="text-slate-400">Expenses</span><p className="font-medium">R{exp.toFixed(2)}</p></div>
                                  <div><span className="text-slate-400">Debt</span><p className="font-medium">R{debt.toFixed(2)}</p></div>
                                  <div><span className="text-slate-400">Disposable</span><p className={`font-bold ${disp >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>R{disp.toFixed(2)}</p></div>
                                </div>
                              ) : null;
                            })()}
                          </div>
                        )}

                        {/* Documents with Download */}
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-1.5"><FileCheck className="w-4 h-4"/>Uploaded Documents</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {[
                              { id: a.id_document_file_id, label: 'ID Document', key: 'id_document' },
                              { id: a.payslip_file_id, label: 'Payslip', key: 'payslip' },
                              { id: a.bank_statement_file_id, label: 'Bank Statement', key: 'bank_statement' },
                              { id: a.proof_of_address_file_id, label: 'Proof of Address', key: 'proof_of_address' },
                            ].map(doc => (
                              <div key={doc.key} className={`flex items-center justify-between p-3 rounded-lg border ${doc.id ? 'bg-white border-emerald-200' : 'bg-slate-100 border-slate-200'}`}>
                                <div className="flex items-center gap-2">
                                  <FileText className={`w-4 h-4 ${doc.id ? 'text-emerald-600' : 'text-slate-400'}`} />
                                  <span className={doc.id ? 'font-medium text-slate-800' : 'text-slate-400'}>{doc.label}</span>
                                </div>
                                {doc.id ? (
                                  <Button size="sm" variant="outline" className="h-8 text-xs" data-testid={`download-${doc.key}`}
                                    onClick={() => handleDocDownload(doc.id, doc.key)}>
                                    <Download className="w-3.5 h-3.5 mr-1" />Download
                                  </Button>
                                ) : (
                                  <span className="text-xs text-slate-400">Not uploaded</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Consent */}
                        <div className="p-3 bg-slate-50 rounded-lg flex flex-wrap gap-3">
                          {[
                            { v: a.consent_credit_check, l: 'Credit Check' },
                            { v: a.consent_terms, l: 'Terms & Conditions' },
                            { v: a.consent_popia, l: 'POPIA' },
                          ].map(c => (
                            <Badge key={c.l} variant="outline" className={c.v ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}>
                              {c.v ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                              {c.l}
                            </Badge>
                          ))}
                        </div>

                        {/* Status Update */}
                        <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                          <Select value={customerAppStatusUpdate} onValueChange={setCustomerAppStatusUpdate}>
                            <SelectTrigger className="w-[180px]" data-testid="app-status-select">
                              <SelectValue placeholder="Change status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="under_review">Under Review</SelectItem>
                              <SelectItem value="awaiting_documents">Awaiting Documents</SelectItem>
                              <SelectItem value="approved">Approve</SelectItem>
                              <SelectItem value="declined">Decline</SelectItem>
                              <SelectItem value="cancelled">Cancel</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button size="sm" disabled={!customerAppStatusUpdate} data-testid="update-app-status-btn"
                            onClick={async () => {
                              try {
                                await axios.put(`${API_URL}/admin/customer-applications/${a.id}/status`,
                                  { status: customerAppStatusUpdate, notes: `Status changed to ${customerAppStatusUpdate}` },
                                  getAuthHeader()
                                );
                                toast.success('Status updated — customer notified via email');
                                setCustomerAppDetail({ open: false, app: null });
                                setCustomerAppStatusUpdate('');
                                fetchCustomerApps();
                              } catch { toast.error('Update failed'); }
                            }}>
                            Update Status
                          </Button>
                        </div>

                        {a.paid_out && (
                          <div className="flex flex-wrap items-center gap-2 p-3 bg-teal-50/60 border border-teal-100 rounded-lg" data-testid="payout-section">
                            <Badge className="bg-teal-600 text-white" data-testid="paid-out-badge">PAID OUT · {a.payout_date}</Badge>
                            {a.payout_by && <span className="text-xs text-slate-500">by {a.payout_by}</span>}
                          </div>
                        )}

                        <VerifyNowPanel appId={a.id} app={a} getAuthHeader={getAuthHeader} onEdited={fetchCustomerApps} />

                        {/* Add Note */}
                        <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                          <Input placeholder="Add admin note..." value={customerAppNote} onChange={e => setCustomerAppNote(e.target.value)} data-testid="app-note-input" />
                          <Button size="sm" disabled={!customerAppNote} data-testid="add-app-note-btn"
                            onClick={async () => {
                              try {
                                await axios.post(`${API_URL}/admin/customer-applications/${a.id}/note`,
                                  { note: customerAppNote }, getAuthHeader()
                                );
                                toast.success('Note added');
                                setCustomerAppNote('');
                                fetchCustomerApps();
                              } catch { toast.error('Failed to add note'); }
                            }}>
                            Add Note
                          </Button>
                        </div>

                        {/* Admin Notes */}
                        {a.admin_notes?.length > 0 && (
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <p className="font-medium mb-2">Admin Notes</p>
                            {a.admin_notes.map((n, i) => (
                              <div key={i} className="text-xs text-slate-600 mb-1">
                                <span className="font-medium">{n.by}</span> ({new Date(n.timestamp).toLocaleString('en-ZA')}): {n.note}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Status History */}
                        {a.status_history?.length > 0 && (
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <p className="font-medium mb-2">Status History</p>
                            {a.status_history.map((h, i) => (
                              <div key={i} className="flex items-center gap-2 text-xs text-slate-600 mb-1">
                                <Badge variant="outline" className="text-[10px] py-0">{h.status}</Badge>
                                <span>{new Date(h.timestamp).toLocaleString('en-ZA')}</span>
                                <span className="text-slate-400">by {h.by}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Per-Application Communications */}
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <ApplicationMessages applicationId={a.id} customerId={a.user_id} getAuthHeader={getAuthHeader} />
                        </div>

                        {/* Live Chat with client */}
                        {a.user_id && (
                          <div className="p-4 bg-slate-50 rounded-lg">
                            <ChatThreadInline userId={a.user_id} userName={a.full_name} getAuthHeader={getAuthHeader} />
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
              </DialogContent>
            </Dialog>

            {/* Approved Amount Dialog */}
            <Dialog open={approvedAmtDialog.open} onOpenChange={o => setApprovedAmtDialog(d => ({ ...d, open: o }))}>
              <DialogContent className="max-w-sm" data-testid="approved-amount-dialog">
                <DialogHeader><DialogTitle>Approved Amount — Loan {approvedAmtDialog.app?.application_number}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <p className="text-xs text-slate-500">
                    Client applied for <strong>R{(approvedAmtDialog.app?.applied_amount || approvedAmtDialog.app?.loan_amount || 0).toLocaleString()}</strong>.
                    Set the amount you are actually approving — fees and total repayment recalculate automatically, and the client sees both amounts in their portal.
                  </p>
                  <div className="space-y-1.5">
                    <Label>Approved Amount (R)</Label>
                    <Input type="number" step="0.01" min="1" value={approvedAmtValue} onChange={e => setApprovedAmtValue(e.target.value)} data-testid="approved-amount-input" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setApprovedAmtDialog({ open: false, app: null })}>Cancel</Button>
                    <Button size="sm" onClick={handleSaveApprovedAmount} disabled={savingApprovedAmt || !approvedAmtValue || parseFloat(approvedAmtValue) <= 0} data-testid="approved-amount-save-btn">
                      {savingApprovedAmt ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* ==================== COMMUNICATIONS ==================== */}
          <TabsContent value="communications" className="space-y-4">
            <Communications getAuthHeader={getAuthHeader} onUnreadChange={setUnreadReplies} />
          </TabsContent>

          {/* ==================== USERS MANAGEMENT ==================== */}
          <TabsContent value="agents" className="space-y-4">
            <AgentsPanel getAuthHeader={getAuthHeader} />
          </TabsContent>

          {user?.role === 'admin' && (
            <TabsContent value="access" className="space-y-4">
              <AccessMatrix getAuthHeader={getAuthHeader} />
            </TabsContent>
          )}

          <TabsContent value="users" className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>User Management</CardTitle>
                    <CardDescription>Manage all users — clients, staff, and admins</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchUsers} data-testid="refresh-users">
                    <RefreshCw className="w-4 h-4 mr-1" />Refresh
                  </Button>
                </div>
                <div className="flex items-center gap-3 mt-3">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Search name, email, phone..." className="pl-9 h-9"
                      value={userSearch} onChange={e => setUserSearch(e.target.value)} data-testid="user-search" />
                  </div>
                  <Select value={userRoleFilter} onValueChange={setUserRoleFilter}>
                    <SelectTrigger className="w-[160px] h-9" data-testid="user-role-filter"><SelectValue placeholder="All Roles" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="loan_officer">Loan Officer</SelectItem>
                      <SelectItem value="approver">Approver</SelectItem>
                      <SelectItem value="collections">Collections</SelectItem>
                      <SelectItem value="finance">Finance</SelectItem>
                      <SelectItem value="support">Support</SelectItem>
                      <SelectItem value="sales_agent">Sales Agent</SelectItem>
                      <SelectItem value="client">Client</SelectItem>
                    </SelectContent>
                  </Select>
                  <Badge variant="outline" className="text-xs whitespace-nowrap">{allUsers.length} users</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-lg border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b">
                        <th className="text-left py-2.5 px-3 font-medium text-slate-600">Name</th>
                        <th className="text-left py-2.5 px-3 font-medium text-slate-600">Email</th>
                        <th className="text-left py-2.5 px-3 font-medium text-slate-600">Phone</th>
                        <th className="text-left py-2.5 px-3 font-medium text-slate-600">Role</th>
                        <th className="text-center py-2.5 px-3 font-medium text-slate-600">Status</th>
                        <th className="text-left py-2.5 px-3 font-medium text-slate-600">Created</th>
                        <th className="text-right py-2.5 px-3 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allUsers.map(u => {
                        const isActive = u.is_active !== false;
                        return (
                          <tr key={u.id} className={`border-b hover:bg-slate-50/50 ${!isActive ? 'opacity-50 bg-red-50/30' : ''}`} data-testid={`user-row-${u.id}`}>
                            <td className="py-2.5 px-3">
                              <p className="font-medium text-slate-800">{u.full_name}</p>
                              {u.client_ref && <p className="text-[10px] text-slate-400">Ref: {u.client_ref}</p>}
                            </td>
                            <td className="py-2.5 px-3 text-slate-600">{u.email}</td>
                            <td className="py-2.5 px-3 text-slate-600">{u.phone || '—'}</td>
                            <td className="py-2.5 px-3">
                              <Select value={u.role} onValueChange={v => handleChangeRole(u.id, v)}>
                                <SelectTrigger className="h-7 text-xs w-[130px]" data-testid={`role-select-${u.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {allRoles.map(r => (
                                    <SelectItem key={r.name} value={r.name}>{r.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="py-2.5 px-3 text-center">
                              <button onClick={() => handleToggleStatus(u.id, isActive)}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition cursor-pointer ${isActive ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                                data-testid={`toggle-status-${u.id}`}>
                                {isActive ? <><ToggleRight className="w-3.5 h-3.5" />Active</> : <><ToggleLeft className="w-3.5 h-3.5" />Disabled</>}
                              </button>
                            </td>
                            <td className="py-2.5 px-3 text-xs text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                            <td className="py-2.5 px-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                {u.role === 'client' && user?.role === 'admin' && (
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-emerald-700 hover:text-emerald-900" title="Log in as this client" data-testid={`impersonate-${u.id}`}
                                    onClick={() => handleImpersonate(u)}>
                                    <Eye className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`edit-user-${u.id}`}
                                  onClick={() => { setEditUserForm({ full_name: u.full_name || '', email: u.email || '', phone: u.phone || '', id_number: u.id_number || '', street_address: u.street_address || '', suburb: u.suburb || '', city: u.city || '', province: u.province || '', postal_code: u.postal_code || '' }); setEditUserDialog({ open: true, user: u }); }}>
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`reset-pw-${u.id}`}
                                  onClick={() => { setResetPwValue(''); setResetPwDialog({ open: true, user: u }); }}>
                                  <Lock className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" data-testid={`delete-user-${u.id}`}
                                  onClick={() => handleDeleteUser(u.id, u.full_name)}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {allUsers.length === 0 && (
                        <tr><td colSpan={7} className="text-center py-8 text-slate-400">No users found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Edit User Profile Dialog */}
            <Dialog open={editUserDialog.open} onOpenChange={o => setEditUserDialog({ open: o, user: o ? editUserDialog.user : null })}>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Edit Profile — {editUserDialog.user?.full_name}</DialogTitle></DialogHeader>
                <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Full Name</Label><Input value={editUserForm.full_name || ''} onChange={e => setEditUserForm(p => ({ ...p, full_name: e.target.value }))} data-testid="edit-user-name" /></div>
                    <div className="space-y-1"><Label className="text-xs">Email</Label><Input value={editUserForm.email || ''} onChange={e => setEditUserForm(p => ({ ...p, email: e.target.value }))} data-testid="edit-user-email" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Phone</Label><Input value={editUserForm.phone || ''} onChange={e => setEditUserForm(p => ({ ...p, phone: e.target.value }))} data-testid="edit-user-phone" /></div>
                    <div className="space-y-1"><Label className="text-xs">ID Number</Label><Input value={editUserForm.id_number || ''} onChange={e => setEditUserForm(p => ({ ...p, id_number: e.target.value }))} data-testid="edit-user-id" /></div>
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Street Address</Label><Input value={editUserForm.street_address || ''} onChange={e => setEditUserForm(p => ({ ...p, street_address: e.target.value }))} /></div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Suburb</Label><Input value={editUserForm.suburb || ''} onChange={e => setEditUserForm(p => ({ ...p, suburb: e.target.value }))} /></div>
                    <div className="space-y-1"><Label className="text-xs">City</Label><Input value={editUserForm.city || ''} onChange={e => setEditUserForm(p => ({ ...p, city: e.target.value }))} /></div>
                    <div className="space-y-1"><Label className="text-xs">Province</Label><Input value={editUserForm.province || ''} onChange={e => setEditUserForm(p => ({ ...p, province: e.target.value }))} /></div>
                  </div>
                  <div className="space-y-1 max-w-[150px]"><Label className="text-xs">Postal Code</Label><Input value={editUserForm.postal_code || ''} onChange={e => setEditUserForm(p => ({ ...p, postal_code: e.target.value }))} /></div>
                  <Button className="w-full mt-2" onClick={handleSaveUserProfile} data-testid="save-user-profile-btn">Save Changes</Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Reset Password Dialog */}
            <Dialog open={resetPwDialog.open} onOpenChange={o => { setResetPwDialog({ open: o, user: o ? resetPwDialog.user : null }); if (!o) { setResetPwValue(''); setTempPwResult(null); } }}>
              <DialogContent>
                <DialogHeader><DialogTitle>Reset Password — {resetPwDialog.user?.full_name}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <p className="text-xs text-slate-500 bg-slate-50 rounded px-2 py-1.5">Passwords are encrypted and can never be viewed — generate a temporary one instead and share it with the client. They can change it after logging in.</p>
                  {tempPwResult ? (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-2" data-testid="temp-password-box">
                      <p className="text-xs font-semibold text-emerald-700">TEMPORARY PASSWORD</p>
                      <div className="flex items-center gap-2">
                        <code className="text-2xl font-bold font-mono text-emerald-800" data-testid="temp-password-value">{tempPwResult.temp_password}</code>
                        <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(tempPwResult.temp_password); toast.success('Copied'); }} data-testid="copy-temp-pw-btn">Copy</Button>
                      </div>
                      <p className="text-xs text-emerald-700">
                        {tempPwResult.sent ? `✓ Sent to the client via ${tempPwResult.channel?.toUpperCase()}` : tempPwResult.error ? `⚠ Not sent: ${tempPwResult.error} — share it with the client yourself` : 'Not sent — share it with the client yourself'}
                      </p>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button className="flex-1" onClick={() => handleGenerateAndSend(true)} disabled={generatingPw} data-testid="generate-send-pw-btn">
                        {generatingPw ? 'Working…' : 'Generate & SMS to Client'}
                      </Button>
                      <Button variant="outline" onClick={() => handleGenerateAndSend(false)} disabled={generatingPw} data-testid="generate-only-pw-btn">
                        Generate Only
                      </Button>
                    </div>
                  )}
                  <div className="relative"><div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div><div className="relative flex justify-center"><span className="bg-white px-2 text-[11px] text-slate-400">or set one manually</span></div></div>
                  <div className="space-y-1">
                    <Label>New Password</Label>
                    <Input type="password" value={resetPwValue} onChange={e => setResetPwValue(e.target.value)} placeholder="Min 6 characters" data-testid="reset-pw-input" />
                  </div>
                  <Button variant="outline" className="w-full" onClick={handleResetPassword} disabled={resetPwValue.length < 6} data-testid="reset-pw-btn">Reset Password</Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Master Password (admin only) */}
            {user?.role === 'admin' && masterPwStatus && (
              <Card className="border-amber-200" data-testid="master-password-card">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base"><KeyRound className="w-4 h-4 text-amber-600" />Master Password</CardTitle>
                  <CardDescription>One password that logs into ANY client account on the client portal — for support purposes. It never works on admin or staff accounts, and every use is recorded in the Audit trail.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={masterPwStatus.is_set ? 'bg-emerald-100 text-emerald-700 border-0' : 'bg-slate-100 text-slate-500 border-0'} data-testid="master-pw-status">
                      {masterPwStatus.is_set ? 'Active' : 'Not set'}
                    </Badge>
                    {masterPwStatus.updated_at && <span className="text-xs text-slate-400">last changed {new Date(masterPwStatus.updated_at).toLocaleString()}</span>}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Input type="password" value={masterPwValue} onChange={e => setMasterPwValue(e.target.value)}
                      placeholder="New master password (min 8 characters)" className="max-w-xs" data-testid="master-pw-input" />
                    <Button size="sm" onClick={handleSaveMasterPw} disabled={savingMasterPw || masterPwValue.length < 8} data-testid="master-pw-save-btn">
                      {savingMasterPw ? 'Saving…' : masterPwStatus.is_set ? 'Change' : 'Set Master Password'}
                    </Button>
                    {masterPwStatus.is_set && (
                      <Button size="sm" variant="outline" className="text-red-600" onClick={handleDisableMasterPw} data-testid="master-pw-disable-btn">Disable</Button>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-400">Usage: go to the client login page, enter the client's email + the master password.</p>
                </CardContent>
              </Card>
            )}

            {/* Roles & Permissions Management */}
            <Card data-testid="roles-management">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2"><ShieldPlus className="w-5 h-5 text-[#0f2b4a]" />Roles & Permissions</CardTitle>
                    <CardDescription>Define roles and control which modules each role can access</CardDescription>
                  </div>
                  <Button size="sm" data-testid="create-role-btn"
                    onClick={() => {
                      setCreateRoleMode(true);
                      const defaultMods = {};
                      availableModules.forEach(m => { defaultMods[m.key] = false; });
                      setRoleForm({ name: '', label: '', modules: defaultMods });
                      setEditRoleDialog({ open: true, role: null });
                    }}>
                    <PlusCircle className="w-4 h-4 mr-1" />New Role
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3">
                  {allRoles.map(role => (
                    <div key={role.id} className={`flex items-center justify-between p-3 rounded-lg border ${role.is_system ? 'bg-[#0f2b4a]/5 border-[#0f2b4a]/20' : 'bg-white'}`} data-testid={`role-card-${role.name}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm text-slate-800">{role.label}</span>
                          <Badge variant="outline" className="text-[10px]">{role.name}</Badge>
                          {role.is_system && <Badge className="bg-[#0f2b4a] text-[10px]">System</Badge>}
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {availableModules.map(m => (
                            <span key={m.key} className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${
                              role.modules?.[m.key] ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400 line-through'
                            }`}>{m.label.split('(')[0].trim()}</span>
                          ))}
                        </div>
                      </div>
                      {!role.is_system && (
                        <div className="flex items-center gap-1 ml-3">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`edit-role-${role.name}`}
                            onClick={() => {
                              setCreateRoleMode(false);
                              setRoleForm({ name: role.name, label: role.label, modules: { ...role.modules } });
                              setEditRoleDialog({ open: true, role });
                            }}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" data-testid={`delete-role-${role.name}`}
                            onClick={() => handleDeleteRole(role.id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                  {allRoles.length === 0 && <p className="text-center text-slate-400 py-4 text-sm">No roles defined</p>}
                </div>
              </CardContent>
            </Card>

            {/* Edit/Create Role Dialog */}
            <Dialog open={editRoleDialog.open} onOpenChange={o => { setEditRoleDialog({ open: o, role: o ? editRoleDialog.role : null }); if (!o) setCreateRoleMode(false); }}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{createRoleMode ? 'Create New Role' : `Edit Role — ${editRoleDialog.role?.label}`}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {createRoleMode && (
                    <div className="space-y-1">
                      <Label className="text-xs">Role Name (unique key)</Label>
                      <Input value={roleForm.name} onChange={e => setRoleForm(p => ({ ...p, name: e.target.value.toLowerCase().replace(/\s+/g, '_') }))} placeholder="e.g. senior_clerk" data-testid="role-name-input" />
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label className="text-xs">Display Label</Label>
                    <Input value={roleForm.label} onChange={e => setRoleForm(p => ({ ...p, label: e.target.value }))} placeholder="e.g. Senior Clerk" data-testid="role-label-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Module Access</Label>
                    <div className="space-y-1.5 p-3 bg-slate-50 rounded-lg border">
                      {availableModules.map(m => (
                        <label key={m.key} className="flex items-center justify-between p-2 rounded hover:bg-white cursor-pointer transition" data-testid={`module-toggle-${m.key}`}>
                          <div>
                            <p className="text-sm font-medium text-slate-700">{m.label}</p>
                            <p className="text-[10px] text-slate-400">{m.description}</p>
                          </div>
                          <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                            checked={!!roleForm.modules?.[m.key]}
                            onChange={e => setRoleForm(p => ({ ...p, modules: { ...p.modules, [m.key]: e.target.checked } }))}
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                  <Button className="w-full" onClick={handleSaveRole} disabled={createRoleMode && (!roleForm.name || !roleForm.label)} data-testid="save-role-btn">
                    {createRoleMode ? 'Create Role' : 'Save Changes'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* ==================== SETTINGS ==================== */}
          <TabsContent value="settings" className="space-y-6">
            <AutoDeclineCard getAuthHeader={getAuthHeader} />
            <VerifyNowSettings getAuthHeader={getAuthHeader} />
            <MigrationCard getAuthHeader={getAuthHeader} />
            <ChatSettings getAuthHeader={getAuthHeader} />
            {/* Apply Now Redirect Link */}
            <Card className="border-l-4 border-l-emerald-500" data-testid="apply-link-settings-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Apply Now Redirect Link</CardTitle>
                <p className="text-xs text-muted-foreground">Set where the &quot;Apply Now&quot; button sends customers</p>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <Input
                    value={applyLink}
                    onChange={e => setApplyLink(e.target.value)}
                    placeholder="https://www.2k2day.co.za"
                    className="flex-1"
                    data-testid="apply-link-input"
                  />
                  <Button onClick={saveApplyLink} disabled={applyLinkSaving} size="sm" data-testid="save-apply-link-btn">
                    {applyLinkSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    <span className="ml-1">Save</span>
                  </Button>
                </div>
                {applyLink && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Current: <a href={applyLink} target="_blank" rel="noopener noreferrer" className="text-emerald-600 underline">{applyLink}</a>
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Loan Products */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Loan Products</CardTitle>
                  <Button size="sm" onClick={() => setProductDialog({ open: true })}><Plus className="w-4 h-4 mr-1" />Add Product</Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {products.map((p) => (
                    <div key={p.id} className="p-4 rounded-xl border bg-slate-50">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold">{p.name}</h4>
                          <p className="text-xs text-slate-500">{p.code}</p>
                        </div>
                        <Badge className={p.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100'}>{p.is_active ? 'Active' : 'Inactive'}</Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm mt-3">
                        <div><span className="text-slate-500">Amount:</span> {formatCurrency(p.min_amount)}-{formatCurrency(p.max_amount)}</div>
                        <div><span className="text-slate-500">Interest:</span> {p.interest_rate}%</div>
                        <div><span className="text-slate-500">Init Fee:</span> {formatCurrency(p.initiation_fee)}</div>
                        <div><span className="text-slate-500">Service:</span> {formatCurrency(p.service_fee)}</div>
                        <div><span className="text-slate-500">Term:</span> {p.default_term_days} days</div>
                        <div><span className="text-slate-500">Penalty:</span> {p.late_penalty_rate}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Staff */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Staff Members</CardTitle>
                  <Button size="sm" onClick={() => setStaffDialog({ open: true })}><UserPlus className="w-4 h-4 mr-1" />Add Staff</Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {staff.map((s) => (
                    <div key={s.id} className="flex items-center justify-between p-3 rounded-lg border">
                      <div>
                        <p className="font-medium">{s.full_name}</p>
                        <p className="text-sm text-slate-500">{s.email}</p>
                      </div>
                      <Badge className={ROLE_COLORS[s.role]}>{s.role?.replace('_', ' ')}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* SEO Settings */}
            {seoSettings && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>SEO Settings</CardTitle>
                      <CardDescription>Manage search engine optimization for your website</CardDescription>
                    </div>
                    <Button size="sm" onClick={saveSeo} disabled={seoSaving} data-testid="save-seo-btn">
                      {seoSaving ? 'Saving...' : 'Save SEO'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Basic Meta */}
                  <div>
                    <h4 className="font-semibold text-sm text-slate-500 uppercase tracking-wider mb-3">Basic Meta Tags</h4>
                    <div className="grid grid-cols-1 gap-4">
                      <div className="space-y-1.5">
                        <Label>Page Title</Label>
                        <Input value={seoSettings.site_title || ''} onChange={e => setSeoSettings(s => ({...s, site_title: e.target.value}))} placeholder="2k2day | Instant Cash Loans" data-testid="seo-site-title" />
                        <p className="text-xs text-slate-400">Recommended: 50-60 characters. Current: {(seoSettings.site_title || '').length}</p>
                      </div>
                      <div className="space-y-1.5">
                        <Label>Meta Description</Label>
                        <Textarea value={seoSettings.meta_description || ''} onChange={e => setSeoSettings(s => ({...s, meta_description: e.target.value}))} rows={3} placeholder="Describe your business..." data-testid="seo-meta-description" />
                        <p className="text-xs text-slate-400">Recommended: 150-160 characters. Current: {(seoSettings.meta_description || '').length}</p>
                      </div>
                      <div className="space-y-1.5">
                        <Label>Meta Keywords</Label>
                        <Textarea value={seoSettings.meta_keywords || ''} onChange={e => setSeoSettings(s => ({...s, meta_keywords: e.target.value}))} rows={2} placeholder="instant cash loans, micro loans..." data-testid="seo-meta-keywords" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label>Canonical URL</Label>
                          <Input value={seoSettings.canonical_url || ''} onChange={e => setSeoSettings(s => ({...s, canonical_url: e.target.value}))} placeholder="https://2k2day.co.za" data-testid="seo-canonical" />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Robots</Label>
                          <Input value={seoSettings.robots || ''} onChange={e => setSeoSettings(s => ({...s, robots: e.target.value}))} placeholder="index, follow" data-testid="seo-robots" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Open Graph */}
                  <div>
                    <h4 className="font-semibold text-sm text-slate-500 uppercase tracking-wider mb-3">Open Graph (Social Sharing)</h4>
                    <div className="grid grid-cols-1 gap-4">
                      <div className="space-y-1.5">
                        <Label>OG Title</Label>
                        <Input value={seoSettings.og_title || ''} onChange={e => setSeoSettings(s => ({...s, og_title: e.target.value}))} placeholder="2k2day - Get Up To R2,000..." data-testid="seo-og-title" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>OG Description</Label>
                        <Textarea value={seoSettings.og_description || ''} onChange={e => setSeoSettings(s => ({...s, og_description: e.target.value}))} rows={2} data-testid="seo-og-description" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>OG Image URL</Label>
                        <Input value={seoSettings.og_image || ''} onChange={e => setSeoSettings(s => ({...s, og_image: e.target.value}))} placeholder="https://2k2day.co.za/og-image.jpg" data-testid="seo-og-image" />
                      </div>
                    </div>
                  </div>

                  {/* Business / Schema */}
                  <div>
                    <h4 className="font-semibold text-sm text-slate-500 uppercase tracking-wider mb-3">Business Info (Schema.org)</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label>Business Name</Label>
                        <Input value={seoSettings.schema_business_name || ''} onChange={e => setSeoSettings(s => ({...s, schema_business_name: e.target.value}))} data-testid="seo-biz-name" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Business Type</Label>
                        <Input value={seoSettings.schema_business_type || ''} onChange={e => setSeoSettings(s => ({...s, schema_business_type: e.target.value}))} data-testid="seo-biz-type" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Phone</Label>
                        <Input value={seoSettings.schema_phone || ''} onChange={e => setSeoSettings(s => ({...s, schema_phone: e.target.value}))} data-testid="seo-biz-phone" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Email</Label>
                        <Input value={seoSettings.schema_email || ''} onChange={e => setSeoSettings(s => ({...s, schema_email: e.target.value}))} data-testid="seo-biz-email" />
                      </div>
                      <div className="space-y-1.5 md:col-span-2">
                        <Label>Address</Label>
                        <Input value={seoSettings.schema_address || ''} onChange={e => setSeoSettings(s => ({...s, schema_address: e.target.value}))} data-testid="seo-biz-address" />
                      </div>
                    </div>
                  </div>

                  {/* Verification */}
                  <div>
                    <h4 className="font-semibold text-sm text-slate-500 uppercase tracking-wider mb-3">Search Engine Verification</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label>Google Verification Code</Label>
                        <Input value={seoSettings.google_verification || ''} onChange={e => setSeoSettings(s => ({...s, google_verification: e.target.value}))} placeholder="Google Search Console code" data-testid="seo-google-verify" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Bing Verification Code</Label>
                        <Input value={seoSettings.bing_verification || ''} onChange={e => setSeoSettings(s => ({...s, bing_verification: e.target.value}))} placeholder="Bing Webmaster code" data-testid="seo-bing-verify" />
                      </div>
                    </div>
                  </div>

                  {/* Geo */}
                  <div>
                    <h4 className="font-semibold text-sm text-slate-500 uppercase tracking-wider mb-3">Regional Settings</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-1.5">
                        <Label>Region Code</Label>
                        <Input value={seoSettings.geo_region || ''} onChange={e => setSeoSettings(s => ({...s, geo_region: e.target.value}))} placeholder="ZA" data-testid="seo-geo-region" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Place Name</Label>
                        <Input value={seoSettings.geo_placename || ''} onChange={e => setSeoSettings(s => ({...s, geo_placename: e.target.value}))} placeholder="South Africa" data-testid="seo-geo-place" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Language</Label>
                        <Input value={seoSettings.language || ''} onChange={e => setSeoSettings(s => ({...s, language: e.target.value}))} placeholder="en-ZA" data-testid="seo-language" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ==================== ACTIVITY LOG ==================== */}
          <TabsContent value="activity-log" className="space-y-4">
            <ActivityLog getAuthHeader={getAuthHeader} />
          </TabsContent>
        </Tabs>
      </main>

      {/* ==================== DIALOGS ==================== */}

      {/* Loan Detail Dialog */}
      <Dialog open={loanDetailDialog.open} onOpenChange={(o) => setLoanDetailDialog({ open: o, loan: o ? loanDetailDialog.loan : null })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Loan Details - {loanDetailDialog.loan?.application_number}</DialogTitle></DialogHeader>
          {loanDetailDialog.loan && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                {getStatusBadge(loanDetailDialog.loan.status)}
                {loanDetailDialog.loan.days_in_arrears > 0 && getArrearsBadge(loanDetailDialog.loan.arrears_status)}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div><p className="text-sm text-slate-500">Borrower</p><p className="font-medium">{loanDetailDialog.loan.borrower_name}</p></div>
                <div><p className="text-sm text-slate-500">Email</p><p className="font-medium">{loanDetailDialog.loan.borrower_email}</p></div>
                <div><p className="text-sm text-slate-500">Phone</p><p className="font-medium">{loanDetailDialog.loan.borrower_phone}</p></div>
                <div><p className="text-sm text-slate-500">Created</p><p className="font-medium">{formatDate(loanDetailDialog.loan.created_at)}</p></div>
                <div><p className="text-sm text-slate-500">Disbursed</p><p className="font-medium">{formatDate(loanDetailDialog.loan.disbursed_at)}</p></div>
                <div><p className="text-sm text-slate-500">Maturity</p><p className="font-medium">{formatDate(loanDetailDialog.loan.maturity_date)}</p></div>
              </div>

              <div className="p-4 rounded-xl bg-slate-50">
                <h4 className="font-semibold mb-3">Balances</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div><p className="text-slate-500">Principal</p><p className="font-semibold">{formatCurrency(loanDetailDialog.loan.loan_amount)}</p></div>
                  <div><p className="text-slate-500">Fees</p><p className="font-semibold">{formatCurrency(loanDetailDialog.loan.total_fees)}</p></div>
                  <div><p className="text-slate-500">Total Due</p><p className="font-semibold text-emerald-700">{formatCurrency(loanDetailDialog.loan.total_amount)}</p></div>
                  <div><p className="text-slate-500">Paid</p><p className="font-semibold text-blue-600">{formatCurrency(loanDetailDialog.loan.amount_paid)}</p></div>
                  <div><p className="text-slate-500">Outstanding</p><p className="font-semibold text-red-600">{formatCurrency(loanDetailDialog.loan.amount_outstanding)}</p></div>
                  <div><p className="text-slate-500">Principal O/S</p><p className="font-semibold">{formatCurrency(loanDetailDialog.loan.principal_outstanding)}</p></div>
                  <div><p className="text-slate-500">Interest O/S</p><p className="font-semibold">{formatCurrency(loanDetailDialog.loan.interest_outstanding)}</p></div>
                  <div><p className="text-slate-500">Fees O/S</p><p className="font-semibold">{formatCurrency(loanDetailDialog.loan.fees_outstanding)}</p></div>
                </div>
              </div>

              {loanDetailDialog.loan.status_history?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2"><History className="w-4 h-4" />Status History</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {loanDetailDialog.loan.status_history.map((h, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm p-2 bg-slate-50 rounded">
                        <span className="text-slate-400 text-xs">{formatDate(h.timestamp)}</span>
                        <Badge variant="outline" className="text-xs">{h.status}</Badge>
                        <span className="text-slate-600">{h.by}</span>
                        {h.notes && <span className="text-slate-500 truncate">- {h.notes}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {loanDetailDialog.loan.notes?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Notes</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {loanDetailDialog.loan.notes.map((n, i) => (
                      <div key={i} className="p-2 bg-slate-50 rounded text-sm">
                        <p>{n.note}</p>
                        <p className="text-xs text-slate-400 mt-1">{n.by} - {formatDate(n.timestamp)} ({n.type})</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialog.open} onOpenChange={(o) => setPaymentDialog({ open: o, loan: o ? paymentDialog.loan : null })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          {paymentDialog.loan && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Outstanding</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(paymentDialog.loan.amount_outstanding)}</p>
              </div>
              <div className="space-y-2">
                <Label>Amount</Label>
                <Input type="number" value={paymentForm.amount} onChange={e => setPaymentForm({ ...paymentForm, amount: e.target.value })} placeholder="0.00" />
              </div>
              <div className="space-y-2">
                <Label>Method</Label>
                <Select value={paymentForm.method} onValueChange={v => setPaymentForm({ ...paymentForm, method: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EFT">EFT</SelectItem>
                    <SelectItem value="DebiCheck">DebiCheck</SelectItem>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="Card">Card</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reference</Label>
                <Input value={paymentForm.reference} onChange={e => setPaymentForm({ ...paymentForm, reference: e.target.value })} />
              </div>
              <Button className="w-full" onClick={handlePayment} disabled={!paymentForm.amount}>Record Payment</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Disbursement Dialog */}
      <Dialog open={disbursementDialog.open} onOpenChange={(o) => setDisbursementDialog({ open: o, loan: o ? disbursementDialog.loan : null })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Disburse Loan</DialogTitle></DialogHeader>
          {disbursementDialog.loan && (
            <div className="space-y-4">
              <div className="p-4 bg-emerald-50 rounded-lg">
                <p className="text-sm text-emerald-600">Approved Amount</p>
                <p className="text-2xl font-bold text-emerald-700">{formatCurrency(disbursementDialog.loan.loan_amount)}</p>
              </div>
              <div className="space-y-2">
                <Label>Disbursement Amount</Label>
                <Input type="number" value={disbursementAmount} onChange={e => setDisbursementAmount(e.target.value)} />
              </div>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={handleDisbursement} disabled={!disbursementAmount}>
                <Send className="w-4 h-4 mr-2" />Confirm Disbursement
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Decline Status Dialog */}
      <Dialog open={statusDialog.open} onOpenChange={(o) => setStatusDialog({ open: o, loan: o ? statusDialog.loan : null, newStatus: statusDialog.newStatus })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Decline Application</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Reason</Label>
              <Select value={declineReason} onValueChange={setDeclineReason}>
                <SelectTrigger><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="income_insufficient">Income Insufficient</SelectItem>
                  <SelectItem value="credit_score_low">Credit Score Low</SelectItem>
                  <SelectItem value="existing_debt">Existing Debt</SelectItem>
                  <SelectItem value="document_issues">Document Issues</SelectItem>
                  <SelectItem value="affordability_failed">Affordability Failed</SelectItem>
                  <SelectItem value="blacklisted">Blacklisted</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={statusNotes} onChange={e => setStatusNotes(e.target.value)} placeholder="Additional notes..." />
            </div>
            <Button className="w-full bg-red-600 hover:bg-red-700" onClick={() => updateLoanStatus(statusDialog.loan?.id, 'declined', statusNotes, declineReason)}>
              Confirm Decline
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Note Dialog */}
      <Dialog open={noteDialog.open} onOpenChange={(o) => setNoteDialog({ open: o, loan: o ? noteDialog.loan : null })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Note</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={noteType} onValueChange={setNoteType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="call">Call</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea value={noteText} onChange={e => setNoteText(e.target.value)} rows={4} />
            </div>
            <Button className="w-full" onClick={handleAddNote} disabled={!noteText}>Add Note</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* PTP Dialog */}
      <Dialog open={ptpDialog.open} onOpenChange={(o) => setPtpDialog({ open: o, loan: o ? ptpDialog.loan : null })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Promise to Pay</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Promised Amount</Label>
              <Input type="number" value={ptpForm.amount} onChange={e => setPtpForm({ ...ptpForm, amount: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Promised Date</Label>
              <Input type="date" value={ptpForm.date} onChange={e => setPtpForm({ ...ptpForm, date: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={ptpForm.notes} onChange={e => setPtpForm({ ...ptpForm, notes: e.target.value })} />
            </div>
            <Button className="w-full" onClick={handlePTP} disabled={!ptpForm.amount || !ptpForm.date}>Record PTP</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Product Dialog */}
      <Dialog open={productDialog.open} onOpenChange={(o) => setProductDialog({ open: o })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Create Loan Product</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Name</Label><Input value={newProduct.name} onChange={e => setNewProduct({ ...newProduct, name: e.target.value })} /></div>
              <div className="space-y-2"><Label>Code</Label><Input value={newProduct.code} onChange={e => setNewProduct({ ...newProduct, code: e.target.value })} placeholder="ML-30" /></div>
            </div>
            <div className="space-y-2"><Label>Description</Label><Input value={newProduct.description} onChange={e => setNewProduct({ ...newProduct, description: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Min Amount</Label><Input type="number" value={newProduct.min_amount} onChange={e => setNewProduct({ ...newProduct, min_amount: parseFloat(e.target.value) })} /></div>
              <div className="space-y-2"><Label>Max Amount</Label><Input type="number" value={newProduct.max_amount} onChange={e => setNewProduct({ ...newProduct, max_amount: parseFloat(e.target.value) })} /></div>
              <div className="space-y-2"><Label>Default</Label><Input type="number" value={newProduct.default_amount} onChange={e => setNewProduct({ ...newProduct, default_amount: parseFloat(e.target.value) })} /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Interest %</Label><Input type="number" value={newProduct.interest_rate} onChange={e => setNewProduct({ ...newProduct, interest_rate: parseFloat(e.target.value) })} /></div>
              <div className="space-y-2"><Label>Init Fee</Label><Input type="number" value={newProduct.initiation_fee} onChange={e => setNewProduct({ ...newProduct, initiation_fee: parseFloat(e.target.value) })} /></div>
              <div className="space-y-2"><Label>Service Fee</Label><Input type="number" value={newProduct.service_fee} onChange={e => setNewProduct({ ...newProduct, service_fee: parseFloat(e.target.value) })} /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Min Term (days)</Label><Input type="number" value={newProduct.min_term_days} onChange={e => setNewProduct({ ...newProduct, min_term_days: parseInt(e.target.value) })} /></div>
              <div className="space-y-2"><Label>Max Term (days)</Label><Input type="number" value={newProduct.max_term_days} onChange={e => setNewProduct({ ...newProduct, max_term_days: parseInt(e.target.value) })} /></div>
              <div className="space-y-2"><Label>Default Term</Label><Input type="number" value={newProduct.default_term_days} onChange={e => setNewProduct({ ...newProduct, default_term_days: parseInt(e.target.value) })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Schedule Frequency</Label>
                <Select value={newProduct.schedule_frequency} onValueChange={v => setNewProduct({ ...newProduct, schedule_frequency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="bi_weekly">Bi-Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Allocation Order</Label>
                <Select value={newProduct.allocation_order} onValueChange={v => setNewProduct({ ...newProduct, allocation_order: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fees_interest_principal">Fees → Interest → Principal</SelectItem>
                    <SelectItem value="principal_interest_fees">Principal → Interest → Fees</SelectItem>
                    <SelectItem value="pro_rata">Pro-Rata</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button className="w-full" onClick={handleCreateProduct}>Create Product</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Staff Dialog */}
      <Dialog open={staffDialog.open} onOpenChange={(o) => setStaffDialog({ open: o })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Staff Member</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Full Name</Label><Input value={newStaff.full_name} onChange={e => setNewStaff({ ...newStaff, full_name: e.target.value })} /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={newStaff.email} onChange={e => setNewStaff({ ...newStaff, email: e.target.value })} /></div>
            <div className="space-y-2"><Label>Phone</Label><Input value={newStaff.phone} onChange={e => setNewStaff({ ...newStaff, phone: e.target.value })} /></div>
            <div className="space-y-2"><Label>Password</Label><Input type="password" value={newStaff.password} onChange={e => setNewStaff({ ...newStaff, password: e.target.value })} /></div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={newStaff.role} onValueChange={v => setNewStaff({ ...newStaff, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="loan_officer">Loan Officer</SelectItem>
                  <SelectItem value="approver">Approver/Underwriter</SelectItem>
                  <SelectItem value="collections">Collections Agent</SelectItem>
                  <SelectItem value="finance">Finance</SelectItem>
                  <SelectItem value="support">Customer Support</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full" onClick={handleCreateStaff}>Create Staff</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <span className="text-slate-500 text-xs">{label}</span>
      <p className="font-medium text-slate-800">{value || '—'}</p>
    </div>
  );
}
